import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, Subscription } from 'rxjs';

import { KeycloakService } from 'keycloak-angular';

import { ConfigService } from '../../shared-services/config.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../../shared-services/environment-config.token';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { resolveSelfServiceApiUrl } from '../../self-service-visualization/shared/self-service-api.utils';
import { DrillDownComponent, DrillDownSaveEvent } from '../drill-down/drill-down.component';
import { DvmPopupComponent } from '../dvm-popup/dvm-popup.component';
import { buildTableModel } from './table-model.builder';
import { applyDrillHeaderGrouping, drillTotalsAtBottom, normalizeDrillTotalsPlacement } from './drilldown-client-tree.utils';
import type {
  TableMatrixCapabilities,
  TableMatrixCellActionEvent,
  TableMatrixNavigateEvent,
  TableMatrixNavigation,
  TableMatrixSaveEvent,
  TableMatrixSaveRow,
  TableMatrixPayloadTemplate,
  TableMatrixEndpointKey,
  TableMatrixWidgetData,
  TableMatrixWorkflowEvent,
  TableModel,
  TableModelColumn,
  TableModelRow,
} from './unified-table-matrix-chart.types';

/**
 * Unified Table & Matrix — ONE component for the whole table family (Normal
 * read-only / Editable CRUD / Drill-Down analytical), config-driven exactly like
 * the unified Bar & Column and Scatter & Bubble families. `data.capabilities`
 * (+ `typeFlags`) selects behaviour — no type-name string checks.
 *
 * - Normal / Editable render CLIENT-SIDE from cached rows via the pure
 *   `buildTableModel`; this component renders the model as an HTML table and owns
 *   search / sort / pagination / column-visibility / (basic) inline editing.
 * - Drill-Down delegates to the existing `lib-drill-down` (its `[data]` is passed
 *   through under `capabilities.drilldownData`), reusing the proven tree renderer.
 */
@Component({
  selector: 'app-unified-table-matrix-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, DrillDownComponent, DvmPopupComponent],
  templateUrl: './unified-table-matrix-chart.component.html',
  styleUrl: './unified-table-matrix-chart.component.scss',
})
export class UnifiedTableMatrixChartComponent implements AfterViewInit, AfterViewChecked, OnChanges, OnDestroy {
  @Input() data!: TableMatrixWidgetData | Record<string, unknown>;

  /** Emitted on row click when `capabilities.emitSelection` is on. */
  @Output() selection = new EventEmitter<{ category: string; value: string }>();

  /** Emitted when the user clicks Refresh — the parent re-fetches/re-computes. */
  @Output() refresh = new EventEmitter<void>();

  /** Emitted on row click when `capabilities.crossFilter` is on — the column +
   *  the FULL current selection (multi-select). Plugs into the DVM cross-filter. */
  @Output() crossFilterChange = new EventEmitter<{ column: string; value: string | null; values: string[] }>();

  /** Emitted when the user clicks Navigate (navigation.enabled) with a selected
   *  cell value — the parent routes to the target dashboard with the filter. */
  @Output() navigate = new EventEmitter<TableMatrixNavigateEvent>();

  /** (Editable) Emitted on Save All / per-row save / auto-save with the payload.
   *  When a save URL is configured on the tile the component POSTs directly;
   *  otherwise it emits this so the parent can wire the endpoint. */
  @Output() save = new EventEmitter<TableMatrixSaveEvent>();

  /** (Editable) Emitted when a `button`/`hyperlink` cell control is actioned. */
  @Output() cellAction = new EventEmitter<TableMatrixCellActionEvent>();

  /** (Editable) Emitted for a workflow action (submit/approve/reject/…). */
  @Output() workflow = new EventEmitter<TableMatrixWorkflowEvent>();

  /** (Editable) Emitted ONCE per save batch after the component's OWN POST(s)
   *  to a configured save endpoint succeed (a custom per-row template fans a
   *  Save-all out into one POST per row - this fires when the last one lands).
   *  Never emitted for the no-URL path, where `save` hands persistence to the
   *  host. Hosts bind this to re-fetch rows: a rule-update service usually
   *  derives OTHER columns server-side (e.g. realized cost from an edited
   *  percent), and the component only ever holds the client's edited copy. */
  @Output() saved = new EventEmitter<void>();

  /** Emitted when the user drag-reorders columns — the new column-key order.
   *  The host (builder) persists it into capabilities.columns so it survives
   *  publish / reload (runtime `columnOrder` alone does not). */
  @Output() columnOrderChange = new EventEmitter<string[]>();

  @ViewChild('tmHost', { static: false }) private hostRef?: ElementRef<HTMLDivElement>;
  @ViewChild('tmFileInput', { static: false }) private fileInputRef?: ElementRef<HTMLInputElement>;

  private configService = inject(ConfigService);
  private http = inject(HttpClient);
  /** Optional: this table also renders in the builder preview and in tests,
   *  where there is no auth context — user tokens then resolve to ''. */
  private readonly keycloak = inject(KeycloakService, { optional: true });
  private environmentConfig = inject(ENVIRONMENT_CONFIG, { optional: true }) as EnvironmentConfig | null;
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private reflowSub?: Subscription;
  private lastRows: Array<Record<string, unknown>> = [];

  // ── Editable (Application Table) state ───────────────────────────────────────
  /** The ORIGINAL rows as first rendered — Cancel restores to this. */
  private originalRows: Array<Record<string, unknown>> = [];
  /** Per-row validation errors keyed by rowId → { field: message }. */
  rowErrors: Record<string, Record<string, string>> = {};
  /** Undo / redo snapshots of the working rows (deep-ish clones). */
  private undoStack: Array<Array<Record<string, unknown>>> = [];
  private redoStack: Array<Array<Record<string, unknown>>> = [];
  /** Clipboard for copy/paste row. */
  private rowClipboard: Record<string, unknown> | null = null;
  /** Monotonic counter for new-row ids (deterministic within a session). */
  private newRowSeq = 0;

  /** Upload in-flight flag + last status message (for the toolbar). */
  uploading = false;
  uploadMessage = '';
  uploadMessageTone: 'info' | 'success' | 'error' = 'info';
  saveNotice = '';
  saveNoticeTone: 'success' | 'error' = 'success';
  private saveNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  /** Toolbar full-screen toggle state, flipped by toggleFullScreen(). */
  fullScreen = false;
  /** Grouped/Tree: rowKeys the user has collapsed (their descendants hidden). */
  collapsedKeys = new Set<string>();

  // ── Rendered state ─────────────────────────────────────────────────────────
  model: TableModel = { columns: [], rows: [], empty: true };
  /** Column keys currently hidden (column-management). */
  hiddenColumns = new Set<string>();
  /** User-reordered column keys (empty = model order). */
  columnOrder: string[] = [];
  /** User-resized widths per column key (px). */
  columnWidths: Record<string, number> = {};
  private resizeState: { key: string; startX: number; startWidth: number } | null = null;
  /** Global search text. */
  searchText = '';
  /** Active sort: column key + direction (kept for single-sort compat). */
  sortKey = '';
  sortDir: 'asc' | 'desc' | '' = '';
  /** Multi-column sort: ordered list of {key,dir}. Shift-click appends. */
  sortKeys: Array<{ key: string; dir: 'asc' | 'desc' }> = [];
  /** Per-column search text (column search filter row). */
  columnSearch: Record<string, string> = {};
  /** Whether the per-column search filter row is shown. */
  showColumnSearch = false;
  /** Advanced per-column filters: { field: {op, value, value2?} }. */
  advancedFilters: Record<string, { op: string; value: string; value2?: string }> = {};
  /** Which column's advanced-filter popover is open (empty = none). */
  advancedFilterCol = '';
  /** Pagination. */
  pageIndex = 0;
  /** Selected data-row indices (into the filtered list). */
  selectedRows = new Set<number>();
  private crossFilterValues = new Set<string>();
  /** Navigation: the currently-selected cell (column + value + row index). */
  navSelectedColumn = '';
  navSelectedValue = '';
  navSelectedIndex: number | null = null;

  private get cfg(): TableMatrixWidgetData {
    return (this.data || {}) as TableMatrixWidgetData;
  }
  get capabilities(): TableMatrixCapabilities | undefined {
    return this.cfg.capabilities;
  }
  get typeFlags(): Record<string, boolean | undefined> {
    return (this.capabilities?.typeFlags || {}) as Record<string, boolean | undefined>;
  }
  get isDrilldown(): boolean {
    const f = this.typeFlags;
    return f['drilldown'] === true || this.capabilities?.tableType === 'drilldown';
  }
  get isEditable(): boolean {
    const f = this.typeFlags;
    // `cellEditing` = a NORMAL table with individually editable columns — same
    // edit machinery, but the row-CRUD flags are forced off in its capabilities.
    return f['editable'] === true || this.capabilities?.tableType === 'editable' || this.capabilities?.cellEditing === true;
  }
  /** The model shape produced by the pure builder — drives the render branch. */
  get shape(): string { return String(this.model.shape || 'flat'); }
  /** Matrix/Pivot have spanning header groups → render a grouped header row. */
  get hasColumnGroups(): boolean { return Array.isArray(this.model.columnGroups) && this.model.columnGroups.length > 0; }
  /** Grouped/Tree rows can expand/collapse — and so can a HIERARCHICAL pivot
   *  (2+ row fields → drill-down rows carrying `expandable`). Memoized per
   *  model: the template reads this per cell (indent binding). */
  private expandableMemo: { model: TableModel; value: boolean } | null = null;
  get isExpandable(): boolean {
    if (this.shape === 'grouped' || this.shape === 'treegrid') return true;
    if (this.expandableMemo?.model !== this.model) {
      this.expandableMemo = { model: this.model, value: this.model.rows.some((r) => r.expandable === true) };
    }
    return this.expandableMemo.value;
  }
  /** Financial statement — role-based row styling. */
  get isFinancial(): boolean { return this.shape === 'financial'; }
  /** The `[data]` object for the embedded lib-drill-down (Drill-Down type).
   *  The table's Format-popup `decimals` (capabilities.decimals) overrides
   *  whatever was baked into the drill-down data at build time so a live
   *  format edit re-renders with the new round-off. Memoized per
   *  (source, decimals) pair — a fresh object every call would retrigger
   *  lib-drill-down's ngOnChanges on every change-detection cycle. */
  get drilldownData(): Record<string, unknown> | null {
    const source = (this.cfg['drilldownData'] as Record<string, unknown>) || (this.capabilities as unknown as { drilldownData?: Record<string, unknown> })?.drilldownData || null;
    if (!source) return null;
    const decimals = this.capabilities?.decimals;
    // Number scale (Format popup "Number format": none / compact / thousands /
    // millions / billions) — applied by lib-drill-down at render time, so a
    // format edit re-renders "16,531,303" as "16.5M" without a regenerate.
    const numberFormat = this.capabilities?.numberScale || this.capabilities?.numberFormat;
    // Header grouping (capabilities.headerGroupBy) is applied at RENDER time
    // on the baked headers, so a format change — or a tile whose tree was
    // generated the other way round — shows the chosen layout without a
    // regenerate. ABSENT (a widget published before the control existed — it
    // has no config access to read the default from) = the project default,
    // 'column' (the Figma "Mining | Marine | Rail" band; the same default the
    // builder's table-matrix.json control carries), so an already-published
    // dashboard shows it on a plain refresh. An explicit 'measure' baked by
    // the builder keeps the legacy measure-on-top band.
    const headerGroupBy: 'measure' | 'column' = this.capabilities?.headerGroupBy ?? 'column';
    // Totals picker: the sticky bottom total row is hidden unless the
    // placement includes "bottom" (absent on older tiles = both = shown).
    const hideTotalRow = !drillTotalsAtBottom(normalizeDrillTotalsPlacement(this.capabilities?.totalsPlacement));
    // Freeze panes (Format popup "Columns" group) — the same controls the flat
    // table honours, forwarded so lib-drill-down pins its first N visible
    // columns / top N rows. `frozenCount` folds the legacy "Freeze first
    // column" toggle into the count exactly like the flat table does.
    const freezeColumnCount = this.frozenCount;
    const freezeRowCount = this.freezeRowCount;
    const cached = this.drilldownDataMemo;
    if (cached && cached.source === source && cached.decimals === decimals && cached.numberFormat === numberFormat && cached.headerGroupBy === headerGroupBy && cached.hideTotalRow === hideTotalRow
      && cached.freezeColumnCount === freezeColumnCount && cached.freezeRowCount === freezeRowCount) return cached.merged;
    const withDecimals = decimals === undefined || decimals === null ? source : { ...source, decimals };
    const withScale = numberFormat ? { ...withDecimals, numberFormat } : withDecimals;
    const withTotalRow = hideTotalRow ? { ...withScale, hideTotalRow: true } : withScale;
    const withFreeze = (freezeColumnCount > 0 || freezeRowCount > 0)
      ? { ...withTotalRow, freezeColumnCount, freezeRowCount }
      : withTotalRow;
    const merged = applyDrillHeaderGrouping(withFreeze, headerGroupBy) as Record<string, unknown>;
    this.drilldownDataMemo = { source, decimals, numberFormat, headerGroupBy, hideTotalRow, freezeColumnCount, freezeRowCount, merged };
    return merged;
  }
  private drilldownDataMemo: { source: Record<string, unknown>; decimals: number | undefined; numberFormat: string | undefined; headerGroupBy: 'measure' | 'column' | undefined; hideTotalRow: boolean; freezeColumnCount: number; freezeRowCount: number; merged: Record<string, unknown> } | null = null;

  get hostHeight(): string {
    const h = this.cfg.height;
    if (h === undefined || h === null || h === '') return 'var(--dvm-chart-height, 300px)';
    const configuredHeight = typeof h === 'number' || /^\d+$/.test(String(h))
      ? `${h}px`
      : String(h);
    // Embedded dashboard tiles define this variable as 100%, allowing the table
    // to follow the live tile size instead of a stale persisted pixel height.
    return `var(--dvm-chart-height, ${configuredHeight})`;
  }

  // ── Config-driven styling (read from chartDefaults; no baked constants) ──────
  private cd(key: string, fallback: string | number): string | number {
    const v = (this.capabilities?.chartDefaults || {})[key];
    return (v === undefined || v === null) ? fallback : (v as string | number);
  }
  get headerBackground(): string { return String(this.capabilities?.headerBackground || this.cd('headerBackground', '#f1f5f9')); }
  get headerColor(): string { return String(this.capabilities?.headerColor || this.cd('headerColor', '#334155')); }
  get borderColor(): string { return String(this.cd('borderColor', '#cbd5e1')); }
  get rowHeight(): number {
    const compact = String(this.capabilities?.density || 'comfortable') === 'compact';
    return Number(compact ? this.cd('compactRowHeight', 32) : this.cd('rowHeight', 40));
  }
  get fontSize(): number {
    // Per-tile Format override beats the config's chartDefaults size.
    const override = Number(this.capabilities?.fontSize);
    return Number.isFinite(override) && override > 0 ? override : Number(this.cd('fontSize', 13));
  }
  get fontFamily(): string | null { return this.capabilities?.fontFamily || null; }
  /** Only set when the tile chose an explicit size — the drill-down renderer
   *  has its own defaults, so no override = leave it untouched. */
  get fontSizeOverride(): number | null {
    const override = Number(this.capabilities?.fontSize);
    return Number.isFinite(override) && override > 0 ? override : null;
  }
  get totalRowBackground(): string { return String(this.cd('totalRowBackground', '#f8fafc')); }
  get striped(): boolean { return this.capabilities?.striped === true; }
  get stickyHeader(): boolean { return this.capabilities?.stickyHeader !== false; }
  get enableSearch(): boolean { return this.capabilities?.enableSearch === true; }
  get enableColumnSearch(): boolean { return this.capabilities?.enableColumnSearch === true; }
  get enableAdvancedFilter(): boolean { return this.capabilities?.enableAdvancedFilter === true; }
  get enableColumnReorder(): boolean { return this.capabilities?.enableColumnReorder === true; }
  get enableColumnResize(): boolean { return this.capabilities?.enableColumnResize === true; }
  get freezeColumnCount(): number { return Math.max(0, Number(this.capabilities?.freezeColumnCount ?? 0)); }
  /** How many top DATA rows to freeze below the header (sticky body rows). */
  get freezeRowCount(): number { return Math.max(0, Number(this.capabilities?.freezeRowCount ?? 0)); }
  get stickyFooter(): boolean { return this.capabilities?.stickyFooter === true; }
  get virtualScroll(): boolean { return this.capabilities?.virtualScroll === true; }
  get enableSavePrefs(): boolean { return this.capabilities?.enableSavePrefs === true; }
  get enableRefresh(): boolean { return this.capabilities?.enableRefresh === true; }
  get enableExport(): boolean { return this.capabilities?.enableExport === true; }
  get enableExcelExport(): boolean { return this.capabilities?.enableExcelExport === true; }
  get enablePdfExport(): boolean { return this.capabilities?.enablePdfExport === true; }
  get enablePrint(): boolean { return this.capabilities?.enablePrint === true; }

  // ── Editable (Application Table) config getters ─────────────────────────────
  get editMode(): 'cell' | 'row' { return this.capabilities?.editMode === 'row' ? 'row' : 'cell'; }
  get enableRowAdd(): boolean { return this.isEditable && this.capabilities?.enableRowAdd === true; }
  get enableRowDelete(): boolean { return this.isEditable && this.capabilities?.enableRowDelete === true; }
  get enableSoftDelete(): boolean { return this.capabilities?.enableSoftDelete === true; }
  get enableRowDuplicate(): boolean { return this.isEditable && this.capabilities?.enableRowDuplicate === true; }
  get enableRowInsert(): boolean { return this.isEditable && this.capabilities?.enableRowInsert === true; }
  get enableCopyPaste(): boolean { return this.isEditable && this.capabilities?.enableCopyPaste === true; }
  get enableUndoRedo(): boolean { return this.isEditable && this.capabilities?.enableUndoRedo === true; }
  get showRowState(): boolean { return this.isEditable && this.capabilities?.showRowState !== false; }
  get enableValidation(): boolean { return this.isEditable && this.capabilities?.enableValidation !== false; }
  get enableRowSave(): boolean { return this.isEditable && this.capabilities?.enableRowSave === true; }
  get enableBulkEdit(): boolean { return this.isEditable && this.capabilities?.enableBulkEdit === true; }
  get enableAutoSave(): boolean { return this.isEditable && this.capabilities?.enableAutoSave === true; }
  get saveOnRowExit(): boolean { return this.isEditable && this.capabilities?.saveOnRowExit === true; }
  get isRowEditMode(): boolean { return this.isEditable && this.editMode === 'row'; }
  // ── Workflow config ─────────────────────────────────────────────────────────
  get enableWorkflow(): boolean { return this.isEditable && this.capabilities?.enableWorkflow === true; }
  get enableComments(): boolean { return this.isEditable && this.capabilities?.enableComments === true; }
  get enableAttachments(): boolean { return this.isEditable && this.capabilities?.enableAttachments === true; }
  get enableActivityHistory(): boolean { return this.isEditable && (this.capabilities?.enableActivityHistory === true || this.capabilities?.enableAuditTrail === true); }
  /** The workflow actions to show (config list, else the full set). */
  get workflowActions(): string[] {
    const cfg = this.capabilities?.workflowActions;
    return Array.isArray(cfg) && cfg.length ? cfg : ['submit', 'approve', 'reject', 'return', 'assign', 'escalate'];
  }
  /** Any per-row action buttons shown → render the trailing action column. */
  get hasRowActions(): boolean {
    return this.enableRowDelete || this.enableRowDuplicate || this.enableRowInsert || this.enableCopyPaste
      || this.enableRowSave || this.enableComments || this.enableAttachments;
  }
  /** In row-edit mode, which row is currently open for editing (rowId). */
  editingRowKey = '';
  isRowInEdit(row: TableModelRow): boolean {
    return !this.isRowEditMode || (!!row.rowId && row.rowId === this.editingRowKey);
  }
  /** Enter/leave whole-row edit (row-edit mode). Leaving triggers save-on-exit. */
  beginRowEdit(row: TableModelRow, event?: Event): void {
    event?.stopPropagation();
    if (row.rowId) this.editingRowKey = row.rowId;
  }
  endRowEdit(row: TableModelRow): void {
    if (this.editingRowKey && this.editingRowKey === row.rowId) this.editingRowKey = '';
    if (this.saveOnRowExit) this.saveRow(row);
  }
  /** Editable toolbar shows when any row-level operation is enabled. */
  get showEditToolbar(): boolean {
    return this.isEditable && (this.enableRowAdd || this.enableUndoRedo || this.isDirty || this.hasRowActions || this.enableWorkflow || this.enableAutoSave);
  }
  get showSaveAllButton(): boolean {
    return !this.enableAutoSave && this.isDirty && this.hasDirtySaveAllTriggerColumn;
  }
  canUndo(): boolean { return this.undoStack.length > 0; }
  canRedo(): boolean { return this.redoStack.length > 0; }
  /** True once any row is new/modified/deleted (drives Save/Cancel enabled). */
  get isDirty(): boolean {
    return this.lastRows.some((r) => !!r['__state'] && r['__state'] !== 'saved');
  }
  /** Count of rows in each pending state — for the toolbar summary. */
  get dirtyCounts(): { new: number; modified: number; deleted: number } {
    const c = { new: 0, modified: 0, deleted: 0 };
    this.lastRows.forEach((r) => {
      const s = r['__state'];
      if (s === 'new') c.new++;
      else if (s === 'modified') c.modified++;
      else if (s === 'deleted') c.deleted++;
    });
    return c;
  }
  /** Rows that currently have validation errors (for the row-level summary). */
  get errorRowCount(): number { return Object.keys(this.rowErrors).filter((k) => Object.keys(this.rowErrors[k] || {}).length).length; }
  private get hasDirtySaveAllTriggerColumn(): boolean {
    const triggerColumns = new Set(this.model.columns.filter((col) => col.saveAllTrigger).map((col) => col.key));
    if (!triggerColumns.size) return this.isDirty;
    return this.lastRows.some((row) => {
      const state = String(row['__state'] ?? '');
      if (state !== 'new' && state !== 'modified' && state !== 'deleted') return false;
      const dirtyKeys = Array.isArray(row['__dirtyColumns']) ? (row['__dirtyColumns'] as unknown[]).map(String) : [];
      return dirtyKeys.some((key) => triggerColumns.has(key));
    });
  }
  /** Error message for a specific cell (inline error under the input). */
  cellError(row: TableModelRow, key: string): string {
    return (row.rowId && this.rowErrors[row.rowId]?.[key]) || '';
  }
  rowState(row: TableModelRow): string { return String((row.raw?.['__state'] as string) || ''); }
  isRowDeleted(row: TableModelRow): boolean { return this.rowState(row) === 'deleted'; }
  /** Operators for the advanced-filter popover. */
  readonly advancedFilterOps = [
    { value: 'contains', label: 'Contains' },
    { value: 'notcontains', label: 'Does not contain' },
    { value: 'equals', label: 'Equals' },
    { value: 'notequals', label: 'Not equal' },
    { value: 'startswith', label: 'Starts with' },
    { value: 'endswith', label: 'Ends with' },
    { value: 'gt', label: '> greater than' },
    { value: 'gte', label: '≥ greater/equal' },
    { value: 'lt', label: '< less than' },
    { value: 'lte', label: '≤ less/equal' },
    { value: 'between', label: 'Between' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notempty', label: 'Is not empty' },
  ];
  get pageSize(): number {
    const n = this.capabilities?.pageSize ?? Number(this.cd('pageSize', 0));
    return Number.isFinite(n) ? Number(n) : 0;
  }

  // ── File upload (upload a file → compute API → table re-renders) ───────────
  get enableUpload(): boolean { return this.capabilities?.enableUpload === true; }
  get uploadButtonLabel(): string { return String(this.capabilities?.uploadButtonLabel || 'Upload'); }
  get uploadAccept(): string { return String(this.capabilities?.uploadAccept || '.xlsx,.xls,.csv'); }
  /** Where the file POSTs — explicit uploadUrl, else the compute API. */
  get uploadUrl(): string {
    return this.resolveTableApiUrl(
      String(this.capabilities?.uploadUrl || this.capabilities?.computeUrl || '/components/compute'),
      '/components/compute',
    );
  }

  /** Table-only API URL resolution. Existing absolute URLs keep working as-is;
   *  when a table config uses a relative path, join it against the optional
   *  env base URL first, then fall back to the shared resolver. */
  private resolveTableApiUrl(url: string | undefined | null, fallbackPath = ''): string {
    const configured = String(url ?? '').trim();
    if (!configured) return resolveSelfServiceApiUrl(configured, fallbackPath);
    if (/^https?:\/\//i.test(configured)) return configured;

    const envBase = String(this.environmentConfig?.SELF_SERVICE_API_BASE_URL ?? '').trim().replace(/\/$/, '');
    if (!envBase) return resolveSelfServiceApiUrl(configured, fallbackPath);

    const path = configured.startsWith('/') ? configured : `/${configured}`;
    return `${envBase}${path}`;
  }

  // ── Navigation (cell → target dashboard) ─────────────────────────────────────
  get navigation(): TableMatrixNavigation | undefined {
    return this.capabilities?.navigation;
  }
  /** Navigation UI is shown (button visible, cells clickable) as soon as the
   *  feature is enabled — even before a target dashboard is picked, so the user
   *  gets visible feedback. Whether it can actually navigate is `canNavigate`. */
  get navEnabled(): boolean {
    return this.navigation?.enabled === true;
  }
  /** True once a target dashboard is configured (needed to actually route). */
  get navHasTarget(): boolean {
    return !!this.navigation?.targetDashboard;
  }
  /** The column whose value navigation sends (defaults to the first column). */
  get navFilterKey(): string {
    return String(this.navigation?.filterKey || this.model.columns[0]?.key || '');
  }
  /** Columns whose cells are clickable (empty config = the filterKey column only). */
  private get navColumnSet(): Set<string> {
    const cols = this.navigation?.navigableColumns;
    if (Array.isArray(cols) && cols.length) return new Set(cols.map(String));
    return new Set([this.navFilterKey]);
  }
  /** Is this column's cell clickable for navigation? */
  isNavigableColumn(key: string): boolean {
    return this.navEnabled && this.navColumnSet.has(key);
  }
  get navButtonLabel(): string {
    return String(this.navigation?.buttonLabel || 'Navigate');
  }
  /** Tooltip on a navigable cell — tells the user the click navigates. */
  get navCellTitle(): string {
    return this.navHasTarget
      ? `Click to open ${this.navigation?.targetDashboard} filtered on this value`
      : 'Set a target dashboard in this table’s Navigation config to enable navigation';
  }
  get canNavigate(): boolean {
    return this.navEnabled && this.navHasTarget && this.navSelectedValue !== '' && this.navSelectedIndex !== null;
  }
  /** Tooltip/disabled-reason for the Navigate button. */
  get navButtonTitle(): string {
    if (!this.navHasTarget) return 'Pick a target dashboard in the table’s Navigation config first';
    if (this.navSelectedValue === '') return 'Click a highlighted cell to select a value first';
    return 'Navigate with the selected value';
  }

  /** Sticky header with a column-group band (Matrix/Pivot): both header rows
   *  are sticky, so the second row must sit BELOW the band instead of covering
   *  it. The band's rendered height is published as a CSS variable on the
   *  table (read by the SCSS `top`), set directly on the DOM — no Angular
   *  binding, so reading it after every view check is cheap and loop-free. */
  private lastColgroupHeight = -1;
  ngAfterViewChecked(): void {
    const host = this.hostRef?.nativeElement;
    if (!host) return;
    const table = host.querySelector<HTMLTableElement>('table.tm-table');
    if (!table) return;
    const band = table.querySelector<HTMLTableRowElement>('tr.tm-colgroup-row');
    const height = band ? band.offsetHeight : 0;
    if (height === this.lastColgroupHeight) return;
    this.lastColgroupHeight = height;
    table.style.setProperty('--tm-colgroup-h', height + 'px');
  }

  ngAfterViewInit(): void {
    // Resolve the signed-in user once so payload-template tokens ({userId})
    // can be substituted synchronously when a save builds its body.
    void this.resolveUserContext();
    const staticRows = this.resolveStaticRows();
    if (staticRows) { setTimeout(() => this.renderRows(staticRows), 0); }
    else { setTimeout(() => this.loadAndRender(), 0); }
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => { /* HTML table reflows via CSS */ });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].isFirstChange()) return;
    // Format-only refresh vs data update: a capabilities/format rebuild (e.g.
    // the builder folding a Format-control change into the tile) spreads the
    // widget data but keeps the SAME `staticData` reference; a real data
    // update (compute/recompute/upload) always arrives as a NEW rows array.
    // On a format-only refresh re-render the rows we ALREADY have — never let
    // a stale (possibly empty) staticData snapshot clobber fetched/edited
    // rows and blank the table to "No data to display".
    const prev = changes['data'].previousValue as Record<string, unknown> | undefined;
    const curr = this.cfg as unknown as Record<string, unknown>;
    const sameStaticRef = !!prev && prev['staticData'] === curr['staticData'];
    if (sameStaticRef && this.lastRows.length) { this.renderRows(this.lastRows); return; }
    const staticRows = this.resolveStaticRows();
    this.renderRows(staticRows ?? this.lastRows);
  }

  private resolveStaticRows(): Array<Record<string, unknown>> | null {
    const candidate = this.cfg.staticData ?? this.cfg.rows ?? this.cfg.data;
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown })?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : null;
  }

  private loadAndRender(): void {
    const staticRows = this.resolveStaticRows();
    if (staticRows) { this.renderRows(staticRows); return; }
    if (!this.cfg.apiUrl) { this.renderRows([]); return; }
    this.configService.getConfig(this.cfg.apiUrl, this.data)?.subscribe({
      next: (res: unknown) => {
        const raw = (res as { data?: unknown })?.data ?? res;
        this.renderRows(Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : []);
      },
      error: () => this.renderRows([]),
    });
  }

  private renderRows(rows: Array<Record<string, unknown>>): void {
    if (!this.capabilities) return;
    // Editable: work on SHALLOW CLONES, never the caller's row objects — cell
    // edits mutate working rows in place, and aliasing the tile's staticData
    // would write edits (+ __state markers) back into the source data. That
    // poisoned the "pristine" snapshot on any re-render, which made Cancel a
    // no-op and would have baked preview edits into a publish.
    // A genuinely NEW payload (post-save re-fetch, dataset refresh) vs the
    // builder re-rendering the SAME working rows (which may carry edits).
    const freshPayload = rows !== this.lastRows;
    const working = this.isEditable && !this.isDrilldown ? rows.map((r) => ({ ...r })) : rows;
    // Fresh rows after a save may lag the server's commit - re-apply the
    // acknowledged edits until the payload carries them itself.
    if (this.isEditable && !this.isDrilldown) this.overlayPendingSaves(working);
    this.lastRows = working;
    // Row values feed the per-row popup data — drop the memo so a refreshed
    // table doesn't keep serving popups built from the previous values.
    this.rowPopupCache.clear();
    // Drill-Down is rendered by the embedded lib-drill-down — no model needed.
    if (this.isDrilldown) { this.model = { columns: [], rows: [], empty: true }; return; }
    // Editable: snapshot the pristine rows on the FIRST render (used by Cancel)
    // and reset the change-tracking so a fresh data load starts clean.
    // A fresh payload ALSO re-snapshots: after a save + server re-fetch the
    // served rows are the new pristine state, else Cancel would roll back to
    // the pre-save data. Never re-snapshot the same working rows (edits).
    if (this.isEditable && (!this.originalRows.length || freshPayload)) {
      this.originalRows = working.map((r) => ({ ...r }));
      this.undoStack = [];
      this.redoStack = [];
      this.rowErrors = {};
    }
    this.model = buildTableModel(working, this.capabilities);
    // "Start groups expanded" = OFF → seed every expandable row collapsed
    // (grouped / tree / hierarchical pivot); default stays fully expanded.
    this.collapsedKeys = this.capabilities?.groupsExpanded === false
      ? new Set(this.model.rows.filter((r) => r.expandable === true && r.rowKey).map((r) => r.rowKey as string))
      : new Set<string>();
    this.pageIndex = 0;
    // Restore the user's saved view (column order/width/hidden/sort) once the
    // model exists so the keys resolve.
    this.restoreUserPrefs();
  }

  /** Re-run the pure builder over the current working rows (after an editable
   *  mutation) WITHOUT resetting the original snapshot / prefs. */
  private rebuildEditable(): void {
    if (!this.capabilities) return;
    this.model = buildTableModel(this.lastRows, this.capabilities);
  }

  // ── Latest-N-years column filter (all non-drilldown types; the drill-down
  // tree applies the same filter inside lib-drill-down) ───────────────────────
  /** First 4-digit year token in a column's key/label (e.g. "2026H1 Net Sales"
   *  → 2026), or null for a year-less column. */
  private columnYear(col: TableModelColumn): number | null {
    const match = `${col.key} ${col.label ?? ''}`.match(/(?:19|20)\d{2}/);
    return match ? Number(match[0]) : null;
  }
  /** The inclusive [min, max] year window, or null when the filter is off /
   *  no column carries a year. Evaluated per render — published tiles roll
   *  into the new year automatically. Anchors to the HIGHEST YEAR PRESENT by
   *  default (future years included — "latest 3" of 2024–2028 is 2026/27/28);
   *  base 'current' opts into anchoring at the system year instead. */
  private yearWindow(): { min: number; max: number } | null {
    const count = Number(this.capabilities?.yearFilterCount);
    if (!Number.isFinite(count) || count <= 0) return null;
    const years = this.model.columns.map((c) => this.columnYear(c)).filter((y): y is number => y !== null);
    if (!years.length) return null;
    const max = this.capabilities?.yearFilterBase === 'current' ? new Date().getFullYear() : Math.max(...years);
    return { min: max - Math.floor(count) + 1, max };
  }
  /** A column passes when it has no year token, or its year is in the window. */
  private passesYearFilter(col: TableModelColumn, window: { min: number; max: number } | null): boolean {
    if (!window) return true;
    const year = this.columnYear(col);
    return year === null || (year >= window.min && year <= window.max);
  }

  // ── Latest-N-years ROW filter (years as row VALUES, e.g. a Fiscal Year
  // column holding 2024/2025/2026) ────────────────────────────────────────────
  // The year column is `capabilities.yearFilterField` when set, else
  // auto-detected: a column named like "year" whose values are years, else any
  // column where nearly every value is an integer in 1900–2100. Rows whose
  // year value falls outside the latest-N window are hidden (display-level,
  // like the search box — built totals are not recomputed). Rows with a
  // non-year value in that column always stay visible.
  private isYearValue(v: unknown): number | null {
    const n = Number(String(v ?? '').replace(/,/g, '').trim());
    return Number.isInteger(n) && n >= 1900 && n <= 2100 ? n : null;
  }
  /** Memoized per model: the detected year column + the window's top year. */
  private yearRowMemo: { model: TableModel; info: { key: string; max: number } | null } | null = null;
  private yearRowInfo(): { key: string; max: number } | null {
    if (this.yearRowMemo?.model === this.model) return this.yearRowMemo.info;
    let info: { key: string; max: number } | null = null;
    const count = Number(this.capabilities?.yearFilterCount);
    if (Number.isFinite(count) && count > 0) {
      const dataRows = this.model.rows.filter((r) => r.kind === 'data');
      const sample = dataRows.slice(0, 100);
      const yearShare = (key: string): number => {
        const vals = sample.map((r) => r.cells[key]?.value).filter((v) => v !== null && v !== undefined && v !== '');
        if (!vals.length) return 0;
        return vals.filter((v) => this.isYearValue(v) !== null).length / vals.length;
      };
      const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
      const explicit = String(this.capabilities?.yearFilterField || '').trim();
      let key = '';
      if (explicit) {
        const col = this.model.columns.find((c) => norm(c.key) === norm(explicit) || norm(c.label || '') === norm(explicit));
        key = col?.key || '';
      } else {
        const named = this.model.columns.find((c) => /year/i.test(`${c.key} ${c.label ?? ''}`) && yearShare(c.key) >= 0.6);
        key = (named || this.model.columns.find((c) => yearShare(c.key) >= 0.8))?.key || '';
      }
      if (key) {
        const years = dataRows
          .map((r) => this.isYearValue(r.cells[key]?.value))
          .filter((y): y is number => y !== null);
        if (years.length) {
          // Default 'data': latest = highest year present (future years count).
          const max = this.capabilities?.yearFilterBase === 'current' ? new Date().getFullYear() : Math.max(...years);
          info = { key, max };
        }
      }
    }
    this.yearRowMemo = { model: this.model, info };
    return info;
  }
  private passesRowYearFilter(row: TableModelRow): boolean {
    if (row.kind !== 'data') return true;
    const info = this.yearRowInfo();
    if (!info) return true;
    const year = this.isYearValue(row.cells[info.key]?.value ?? row.raw?.[info.key]);
    if (year === null) return true;
    const count = Math.floor(Number(this.capabilities?.yearFilterCount));
    return year >= info.max - count + 1 && year <= info.max;
  }

  // ── Column visibility / order / width ──────────────────────────────────────
  /** Rendered columns. A column is dropped from the UI when its config marks it
   *  `hidden` (config-driven show/hide), when a restored user pref hides it, or
   *  when the latest-N-years filter excludes its year. Dropped columns stay in
   *  `model.columns` on purpose — the save payload is built from that full set,
   *  so hiding/filtering a column never drops its value. */
  get visibleColumns(): TableModelColumn[] {
    const yearWindow = this.yearWindow();
    const shown = this.model.columns.filter((c) => !c.hidden && !this.hiddenColumns.has(c.key) && this.passesYearFilter(c, yearWindow));
    if (!this.columnOrder.length) return shown;
    // Apply the user's column order; any unlisted columns keep their tail order.
    const byKey = new Map(shown.map((c) => [c.key, c]));
    const ordered: TableModelColumn[] = [];
    this.columnOrder.forEach((k) => { const c = byKey.get(k); if (c) { ordered.push(c); byKey.delete(k); } });
    byKey.forEach((c) => ordered.push(c));
    return ordered;
  }
  /** Reset column order / width / hidden back to the model defaults. */
  resetColumns(): void {
    this.columnOrder = [];
    this.columnWidths = {};
    this.hiddenColumns = new Set();
  }
  /** Native HTML5 drag-reorder of header columns. Angular CDK drag-drop does NOT
   *  work on native <table>/<th> (it detaches the cell and the row collapses), so
   *  the header uses draggable <th> + dragstart/dragover/drop instead. */
  dragColKey = '';
  dragOverColKey = '';
  onColDragStart(col: TableModelColumn, event: DragEvent): void {
    if (!this.enableColumnReorder) return;
    this.dragColKey = col.key;
    event.dataTransfer?.setData('text/plain', col.key);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }
  onColDragOver(col: TableModelColumn, event: DragEvent): void {
    if (!this.enableColumnReorder || !this.dragColKey) return;
    event.preventDefault(); // allow drop
    this.dragOverColKey = col.key;
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }
  onColDrop(col: TableModelColumn, event: DragEvent): void {
    if (!this.enableColumnReorder || !this.dragColKey) return;
    event.preventDefault();
    const cols = this.visibleColumns.map((c) => c.key);
    const from = cols.indexOf(this.dragColKey);
    const to = cols.indexOf(col.key);
    if (from > -1 && to > -1 && from !== to) {
      cols.splice(to, 0, cols.splice(from, 1)[0]);
      this.columnOrder = cols;
      // Emit the new order so the host (builder) can persist it into the tile's
      // capabilities.columns — otherwise the reorder is runtime-only and resets
      // to the configured order on publish / reload.
      this.columnOrderChange.emit([...cols]);
    }
    this.dragColKey = '';
    this.dragOverColKey = '';
  }
  onColDragEnd(): void { this.dragColKey = ''; this.dragOverColKey = ''; }
  /** Effective width for a column (user override else model). */
  colWidth(col: TableModelColumn): number {
    return this.columnWidths[col.key] || col.width;
  }
  /** Begin a drag-resize on a column's right edge. */
  startColumnResize(col: TableModelColumn, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeState = { key: col.key, startX: event.clientX, startWidth: this.colWidth(col) };
    const move = (e: MouseEvent) => {
      if (!this.resizeState) return;
      const w = Math.max(48, this.resizeState.startWidth + (e.clientX - this.resizeState.startX));
      this.columnWidths = { ...this.columnWidths, [this.resizeState.key]: w };
    };
    const up = () => {
      this.resizeState = null;
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }
  // ── Freeze columns (multi) ──────────────────────────────────────────────────
  /** How many leftmost columns are frozen (freezeColumnCount, or 1 when the
   *  legacy freezeFirstColumn is on). */
  private get frozenCount(): number {
    if (this.freezeColumnCount > 0) return this.freezeColumnCount;
    return this.capabilities?.freezeFirstColumn === true ? 1 : 0;
  }
  isColFrozen(_col: TableModelColumn, index: number): boolean {
    return index < this.frozenCount;
  }
  /** Cumulative left offset (px) for a frozen column at visible index. */
  frozenLeft(index: number): number {
    let left = 0;
    const cols = this.visibleColumns;
    for (let i = 0; i < index && i < this.frozenCount; i++) left += this.colWidth(cols[i]);
    return left;
  }
  // ── Freeze rows (top data rows pin below the header) ────────────────────────
  /** A DATA row is frozen when its data-index is within freezeRowCount. Only the
   *  flat shape freezes rows (structured shapes keep their hierarchy). `dataIndex`
   *  is the row's position among DATA rows on the current page (0-based). */
  isRowFrozen(row: TableModelRow, dataIndex: number): boolean {
    return this.freezeRowCount > 0 && this.shape === 'flat' && row.kind === 'data' && dataIndex < this.freezeRowCount;
  }
  /** Top offset (px) for a frozen data row: header height + the rows above it.
   *  The sticky header already occupies row 0, so frozen rows stack beneath it. */
  frozenRowTop(dataIndex: number): number {
    const headerRows = 1 + (this.hasColumnGroups ? 1 : 0) + (this.enableColumnSearch && this.showColumnSearch ? 1 : 0);
    const headerOffset = this.stickyHeader ? headerRows * this.rowHeight : 0;
    return headerOffset + dataIndex * this.rowHeight;
  }
  /** The 0-based data-row index of a paged row (only DATA rows count).
   *  Memoized against the current pagedRows array so the per-row lookup the
   *  template does stays O(1) instead of O(n) each call. */
  private dataIndexMemo: { rows: TableModelRow[]; map: Map<TableModelRow, number> } | null = null;
  dataIndexOf(row: TableModelRow): number {
    const rows = this.pagedRows;
    if (!this.dataIndexMemo || this.dataIndexMemo.rows !== rows) {
      const map = new Map<TableModelRow, number>();
      let n = 0;
      for (const r of rows) { if (r.kind === 'data') { map.set(r, n); n++; } }
      this.dataIndexMemo = { rows, map };
    }
    return this.dataIndexMemo.map.get(row) ?? -1;
  }
  /** (Matrix/Pivot) visible columns NOT in any column-group — they get a blank
   *  spanning header cell above them in the group header row. */
  get ungroupedColumns(): TableModelColumn[] {
    const grouped = new Set<string>();
    (this.model.columnGroups || []).forEach((g) => g.keys.forEach((k) => grouped.add(k)));
    return this.visibleColumns.filter((c) => !grouped.has(c.key));
  }
  /** colspan for a column-group's spanning header = its visible member columns. */
  colGroupSpan(grp: { keys: string[] }): number {
    const visible = new Set(this.visibleColumns.map((c) => c.key));
    return grp.keys.filter((k) => visible.has(k)).length || 1;
  }

  // ── Search + column-search + advanced filter + sort ─────────────────────────
  /** A row passes when it matches the GLOBAL search AND every per-column search
   *  AND every advanced filter. */
  private matchesSearch(row: TableModelRow): boolean {
    // Latest-N-years ROW filter (config-driven) — independent of the search
    // toggles below.
    if (!this.passesRowYearFilter(row)) return false;
    // Each filter applies ONLY while its UI is enabled — a stale term left in
    // state after its control is turned off (e.g. "Global search box" toggled
    // off in the Format popup) must not keep filtering rows invisibly.
    // Global search (any cell contains). Match the cell's DISPLAY *or* its raw
    // VALUE — editable cells render from the raw value (an <input>), so its
    // formatted `display` can differ from / lag what the user sees and typed.
    if (this.enableSearch && this.searchText.trim()) {
      const q = this.searchText.trim().toLowerCase();
      if (!Object.values(row.cells).some((c) => this.cellMatches(c, q))) return false;
    }
    // Per-column search (each column's box: that cell contains).
    if (this.enableColumnSearch) {
      for (const key of Object.keys(this.columnSearch)) {
        const term = (this.columnSearch[key] || '').trim().toLowerCase();
        if (term && !this.cellMatches(row.cells[key], term)) return false;
      }
    }
    // Advanced filters (operator + value per column).
    if (this.enableAdvancedFilter) {
      for (const key of Object.keys(this.advancedFilters)) {
        const f = this.advancedFilters[key];
        if (!f || (f.value === '' && f.op !== 'empty' && f.op !== 'notempty')) continue;
        if (!this.advMatch(row.cells[key]?.value, f)) return false;
      }
    }
    return true;
  }
  /** A cell matches a lowercased query when either its formatted display OR its
   *  raw value contains it (editable cells show the raw value, not `display`). */
  private cellMatches(cell: { value?: unknown; display?: string } | undefined, q: string): boolean {
    if (!cell) return false;
    if (String(cell.display ?? '').toLowerCase().includes(q)) return true;
    return String(cell.value ?? '').toLowerCase().includes(q);
  }
  private advMatch(value: unknown, f: { op: string; value: string; value2?: string }): boolean {
    const s = String(value ?? '').toLowerCase();
    const fv = String(f.value ?? '').toLowerCase();
    const num = Number(String(value ?? '').replace(/[^0-9.eE+-]/g, ''));
    const fnum = Number(f.value);
    switch (f.op) {
      case 'contains': return s.includes(fv);
      case 'notcontains': return !s.includes(fv);
      case 'equals': return s === fv;
      case 'notequals': return s !== fv;
      case 'startswith': return s.startsWith(fv);
      case 'endswith': return s.endsWith(fv);
      case 'gt': return Number.isFinite(num) && num > fnum;
      case 'gte': return Number.isFinite(num) && num >= fnum;
      case 'lt': return Number.isFinite(num) && num < fnum;
      case 'lte': return Number.isFinite(num) && num <= fnum;
      case 'between': return Number.isFinite(num) && num >= fnum && num <= Number(f.value2);
      case 'empty': return s === '';
      case 'notempty': return s !== '';
      default: return true;
    }
  }
  /** Sort a column. Shift-click → append/toggle in the multi-sort list;
   *  plain click → single sort (tri-state asc→desc→off). */
  onSort(key: string, event?: MouseEvent): void {
    const multi = !!event?.shiftKey;
    if (multi) {
      const existing = this.sortKeys.find((s) => s.key === key);
      if (!existing) this.sortKeys = [...this.sortKeys, { key, dir: 'asc' }];
      else if (existing.dir === 'asc') this.sortKeys = this.sortKeys.map((s) => s.key === key ? { ...s, dir: 'desc' } : s);
      else this.sortKeys = this.sortKeys.filter((s) => s.key !== key);
      // Keep single-sort fields mirrored to the FIRST multi-sort for the header UI.
      this.sortKey = this.sortKeys[0]?.key || '';
      this.sortDir = this.sortKeys[0]?.dir || '';
    } else {
      if (this.sortKey === key && this.sortKeys.length <= 1) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : this.sortDir === 'desc' ? '' : 'asc';
        this.sortKey = this.sortDir === '' ? '' : key;
      } else { this.sortKey = key; this.sortDir = 'asc'; }
      this.sortKeys = this.sortKey ? [{ key: this.sortKey, dir: this.sortDir as 'asc' | 'desc' }] : [];
    }
    this.pageIndex = 0;
  }
  /** Header sort indicator for a column (▲/▼ + order number for multi-sort). */
  sortIndicator(key: string): string {
    const idx = this.sortKeys.findIndex((s) => s.key === key);
    if (idx < 0) return '';
    const arrow = this.sortKeys[idx].dir === 'asc' ? '▲' : '▼';
    return this.sortKeys.length > 1 ? `${arrow}${idx + 1}` : arrow;
  }
  /** Stable default row order used while the user has NOT sorted: sort by the
   *  first visible column (or a configured column key) so the same data always
   *  renders in the same order no matter how the compute API ordered it.
   *  `defaultSort: 'none'` opts a tile back into verbatim backend order. */
  private get defaultSortKeys(): Array<{ key: string; dir: 'asc' | 'desc' }> {
    const mode = String(this.capabilities?.defaultSort ?? this.cd('defaultSort', 'firstColumn'));
    if (mode === 'none') return [];
    const cols = this.visibleColumns;
    const key = mode !== 'firstColumn' && cols.some((c) => c.key === mode) ? mode : cols[0]?.key || '';
    if (!key) return [];
    const dir: 'asc' | 'desc' = this.capabilities?.defaultSortDir === 'desc' ? 'desc' : 'asc';
    return [{ key, dir }];
  }
  toggleColumnSearch(): void { this.showColumnSearch = !this.showColumnSearch; }
  openAdvancedFilter(key: string, event: Event): void {
    event.stopPropagation();
    this.advancedFilterCol = this.advancedFilterCol === key ? '' : key;
    if (!this.advancedFilters[key]) this.advancedFilters[key] = { op: 'contains', value: '' };
  }
  clearAdvancedFilter(key: string): void {
    delete this.advancedFilters[key];
    this.advancedFilters = { ...this.advancedFilters };
    this.advancedFilterCol = '';
    this.pageIndex = 0;
  }
  applyAdvancedFilter(): void { this.advancedFilters = { ...this.advancedFilters }; this.advancedFilterCol = ''; this.pageIndex = 0; }
  hasAdvancedFilter(key: string): boolean {
    const f = this.advancedFilters[key];
    return !!f && (f.value !== '' || f.op === 'empty' || f.op === 'notempty');
  }
  /** Rows after search + (flat) sort + (structured) collapse-hiding.
   *  Structured shapes (grouped/tree/matrix/pivot/financial) keep their built
   *  order — sorting would destroy the hierarchy — and just apply search +
   *  hide the descendants of collapsed rows. Flat keeps the original behaviour. */
  get processedRows(): TableModelRow[] {
    if (this.shape !== 'flat') {
      return this.model.rows.filter((r) => this.matchesSearch(r) && !this.isRowHidden(r));
    }
    const dataRows = this.model.rows.filter((r) => r.kind === 'data' && this.matchesSearch(r));
    const totalRows = this.model.rows.filter((r) => r.kind !== 'data');
    // Multi-column sort: compare by each sort key in order until a tiebreak.
    // With NO user sort, fall back to a stable default sort so the row order
    // is deterministic — the compute API's response order can differ between
    // otherwise-identical calls (e.g. per filter selection), and the table
    // must not reshuffle when the data itself hasn't changed.
    const activeSort = this.sortKeys.length ? this.sortKeys : this.defaultSortKeys;
    if (activeSort.length) {
      // Editable: rows the user just added stay pinned at the END in insertion
      // order (a default sort would yank a half-typed row to the top).
      const pinNew = !this.sortKeys.length;
      const sortable = pinNew ? dataRows.filter((r) => r.state !== 'new') : dataRows;
      const newRows = pinNew ? dataRows.filter((r) => r.state === 'new') : [];
      sortable.sort((a, b) => {
        for (const s of activeSort) {
          const av = a.cells[s.key]?.value;
          const bv = b.cells[s.key]?.value;
          const an = Number(av), bn = Number(bv);
          let cmp: number;
          if (Number.isFinite(an) && Number.isFinite(bn)) cmp = an - bn;
          else cmp = String(av ?? '').localeCompare(String(bv ?? ''));
          if (cmp !== 0) return s.dir === 'asc' ? cmp : -cmp;
        }
        return 0;
      });
      return [...sortable, ...newRows, ...totalRows];
    }
    return [...dataRows, ...totalRows];
  }
  /** Rows for the current page. Only the FLAT shape paginates (structured shapes
   *  show all rows so the hierarchy stays intact). */
  get pagedRows(): TableModelRow[] {
    const all = this.processedRows;
    if (this.shape !== 'flat' || !this.pageSize) return all;
    const dataRows = all.filter((r) => r.kind === 'data');
    const totalRows = all.filter((r) => r.kind !== 'data');
    const start = this.pageIndex * this.pageSize;
    return [...dataRows.slice(start, start + this.pageSize), ...totalRows];
  }

  // ── Grouped / Tree expand-collapse ───────────────────────────────────────────
  /** A row is hidden when ANY ancestor (via parentKey chain) is collapsed. */
  isRowHidden(row: TableModelRow): boolean {
    if (!this.collapsedKeys.size) return false;
    let parent = row.parentKey;
    const byKey = this.rowsByKey;
    let guard = 0;
    while (parent && guard++ < 64) {
      if (this.collapsedKeys.has(parent)) return true;
      parent = byKey.get(parent)?.parentKey;
    }
    return false;
  }
  private get rowsByKey(): Map<string, TableModelRow> {
    const m = new Map<string, TableModelRow>();
    this.model.rows.forEach((r) => { if (r.rowKey) m.set(r.rowKey, r); });
    return m;
  }
  isRowExpanded(row: TableModelRow): boolean {
    return !!row.rowKey && !this.collapsedKeys.has(row.rowKey);
  }
  toggleExpand(row: TableModelRow, event?: Event): void {
    event?.stopPropagation();
    if (!row.rowKey) return;
    if (this.collapsedKeys.has(row.rowKey)) this.collapsedKeys.delete(row.rowKey);
    else this.collapsedKeys.add(row.rowKey);
    this.collapsedKeys = new Set(this.collapsedKeys);
  }
  /** Left indent (px) for a grouped/tree/financial row by its depth. */
  rowIndent(row: TableModelRow): number {
    return Math.max(0, Number(row.depth ?? 0)) * 18;
  }
  get totalPages(): number {
    if (!this.pageSize) return 1;
    const dataCount = this.processedRows.filter((r) => r.kind === 'data').length;
    return Math.max(1, Math.ceil(dataCount / this.pageSize));
  }
  prevPage(): void { if (this.pageIndex > 0) this.pageIndex -= 1; }
  nextPage(): void { if (this.pageIndex < this.totalPages - 1) this.pageIndex += 1; }

  // ── Row selection + cross-filter ────────────────────────────────────────────
  onRowClick(row: TableModelRow, index: number): void {
    if (row.kind !== 'data' || !row.raw) return;
    const cap = this.capabilities;
    if (!cap) return;
    const multi = String(cap.rowSelection || '') === 'multi';
    if (multi) {
      if (this.selectedRows.has(index)) this.selectedRows.delete(index);
      else this.selectedRows.add(index);
    } else {
      this.selectedRows = new Set(this.selectedRows.has(index) ? [] : [index]);
    }
    this.selectedRows = new Set(this.selectedRows);

    const column = cap.crossFilterKey || this.model.columns[0]?.key || '';
    const value = column ? String(row.raw[column] ?? '') : '';
    if (cap.emitSelection && column) this.selection.emit({ category: column, value });
    if (cap.crossFilter && column && value) {
      if (this.crossFilterValues.has(value)) this.crossFilterValues.delete(value);
      else this.crossFilterValues.add(value);
      this.crossFilterChange.emit({ column, value, values: Array.from(this.crossFilterValues) });
    }
  }
  isRowSelected(index: number): boolean { return this.selectedRows.has(index); }

  // ── Navigation: click the cell VALUE → navigate directly (anchor-style) ─────
  /** Click a navigable cell → navigate IMMEDIATELY with that row's filterKey
   *  value (no separate button). The value sent is always the filterKey column's
   *  value of the clicked row, so any navigable column drives the same filter.
   *  No-op (falls through to normal cell behaviour) until a target dashboard is
   *  configured. */
  onNavCellClick(row: TableModelRow, index: number, column: string, event: Event): void {
    if (!this.navEnabled || row.kind !== 'data' || !row.raw) return;
    if (!this.isNavigableColumn(column)) return;
    event.stopPropagation();
    const key = this.navFilterKey;
    this.navSelectedColumn = key;
    this.navSelectedValue = String(row.raw[key] ?? row.cells[key]?.value ?? '');
    this.navSelectedIndex = index;
    // Anchor behaviour: the click IS the navigate. Highlight the clicked cell
    // FIRST (a brief flash), then navigate — so the user sees which value they
    // picked before the route changes. Only fires once a target is configured;
    // otherwise the cell just stays highlighted (builder-preview feedback).
    if (this.navHasTarget && this.navSelectedValue !== '') {
      setTimeout(() => this.onNavigate(), 180);
    }
  }
  isNavSelected(index: number, column: string): boolean {
    return this.navSelectedIndex === index && this.isNavigableColumn(column);
  }
  /** Emit the navigation event with the selected value. */
  onNavigate(): void {
    if (!this.canNavigate) return;
    const nav = this.navigation!;
    this.navigate.emit({
      targetDashboard: String(nav.targetDashboard || ''),
      targetComponents: Array.isArray(nav.targetComponents) ? nav.targetComponents.map(String) : [],
      filterKey: this.navSelectedColumn || this.navFilterKey,
      filterValue: this.navSelectedValue,
    });
  }

  // ── Drill-Down editable measures → forward through the family's save output ─
  /** The embedded lib-drill-down saved its edited measure cells — reshape the
   *  tree changes into the standard TableMatrixSaveEvent (each change becomes
   *  one 'modified' row addressed by its ancestor path) and emit. The HOST does
   *  the rest exactly like a Normal/Editable save: builder shows the preview
   *  toast; published POSTs to capabilities.saveUrl (else its default save
   *  endpoint) via onTableSave. */
  onDrilldownSave(event: DrillDownSaveEvent): void {
    const changes = event?.changes || [];
    if (!changes.length) return;
    const cap = this.capabilities;
    const rows: TableMatrixSaveRow[] = changes.map((c) => ({
      rowId: c.path.join(' / '),
      state: 'modified',
      values: {
        ...(c.rowValues || {}),
        field: c.field,
        value: c.value,
        oldValue: c.oldValue ?? null,
        path: c.path,
        name: c.name,
        level: c.level,
        // Free-text attribute edits carry kind:'text' so the stored-tree patch
        // keeps the string instead of coercing it to a number.
        ...(c.kind === 'text' ? { kind: 'text' } : {}),
      },
    }));
    const saveEvent: TableMatrixSaveEvent = {
      componentId: cap?.componentId,
      dataset: cap?.dataset,
      saveUrl: (cap?.saveUrl || '').trim() || undefined,
      operation: 'update',
      rows,
    };
    const url = this.resolveTableApiUrl((cap?.updateUrl || cap?.saveUrl || '').trim());
    if (!url) {
      this.save.emit(saveEvent);
      return;
    }
    const urlKey: TableMatrixEndpointKey = cap?.updateUrl ? 'updateUrl' : 'saveUrl';
    const template = this.payloadTemplateFor(urlKey);
    const requests = rows.map((row) => this.http.post(url, template
      ? this.buildCustomPayload(row, row.values, template)
      : saveEvent));
    forkJoin(requests).subscribe({
      next: () => {
        this.setStatusMessage('Saved', 'success');
        this.showSaveNotice('Saved', 'success');
        this.saved.emit();
      },
      error: () => {
        this.setStatusMessage('Not saved', 'error');
        this.showSaveNotice('Not saved', 'error');
      },
    });
  }

  // ── Copy cell ────────────────────────────────────────────────────────────
  copyCell(text: string): void {
    if (navigator?.clipboard) navigator.clipboard.writeText(text).catch(() => { /* ignore */ });
  }

  // ── Editable — write an edited value back to the source row + re-render ──────
  onCellEdit(row: TableModelRow, key: string, value: unknown): void {
    if (!row.raw) return;
    const column = this.model.columns.find((c) => c.key === key);
    const nextValue = column ? this.normalizeEditedCellValue(column, value) : value;
    this.pushUndo();
    this.applyCellValue(row, key, nextValue);
    // Bulk edit / multi-row update: when this row is part of a multi-selection,
    // fill the same value down to every other selected data row.
    if (this.enableBulkEdit && this.selectedRows.size > 1) {
      const paged = this.pagedRows;
      this.selectedRows.forEach((idx) => {
        const target = paged[idx];
        if (target && target !== row && target.kind === 'data' && target.raw) this.applyCellValue(target, key, nextValue);
      });
    }
    if (this.enableValidation) this.validateRow(row);
    this.scheduleAutoSave(row);
  }
  /** Editable numeric controls should reject alphabetic entry at the point of
   *  typing (and pasted text is sanitized separately in normalizeEditedCellValue). */
  onNumericCellKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key;
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowed.includes(key)) return;
    if (/^\d$/.test(key)) return;
    const input = event.target as HTMLInputElement | null;
    if (!input) { event.preventDefault(); return; }
    const value = input.value || '';
    const hasSelection = input.selectionStart !== input.selectionEnd;
    const caretAtStart = (input.selectionStart ?? 0) === 0;
    if (key === '.' && (!value.includes('.') || hasSelection)) return;
    if (key === '-' && caretAtStart && (!value.includes('-') || hasSelection)) return;
    event.preventDefault();
  }
  /** Convert free typing/paste into a numeric-only string for number-like
   *  controls, allowing digits plus one decimal point and one leading minus. */
  private normalizeEditedCellValue(col: TableModelColumn, value: unknown): unknown {
    if (!this.isNumericControl(col.control)) return value;
    if (value === null || value === undefined) return '';
    let text = String(value).replace(/[^\d.-]/g, '');
    let out = '';
    let seenDot = false;
    let seenMinus = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text.charAt(i);
      if (/\d/.test(ch)) { out += ch; continue; }
      if (ch === '.' && !seenDot) { out += ch; seenDot = true; continue; }
      if (ch === '-' && !seenMinus && out.length === 0) { out += ch; seenMinus = true; }
    }
    return out;
  }
  private isNumericControl(control?: string): boolean {
    return control === 'number' || control === 'currency' || control === 'percent';
  }
  /** Write one value into a row's raw + rendered cell and mark it modified. */
  private applyCellValue(row: TableModelRow, key: string, value: unknown): void {
    if (!row.raw) return;
    row.raw[key] = value;
    const dirty = new Set(Array.isArray(row.raw['__dirtyColumns']) ? (row.raw['__dirtyColumns'] as unknown[]).map(String) : []);
    dirty.add(key);
    row.raw['__dirtyColumns'] = Array.from(dirty);
    const cell = row.cells[key];
    if (cell) { cell.value = value; cell.display = value === null || value === undefined ? '' : String(value); }
    if (row.raw['__state'] !== 'new') row.raw['__state'] = 'modified';
    if (row.rowId) row.state = row.raw['__state'] as TableModelRow['state'];
  }
  /** A `button`/`hyperlink` cell was actioned → emit for the parent to handle. */
  onCellActionClick(row: TableModelRow, col: TableModelColumn, event: Event): void {
    event.stopPropagation();
    if (!row.raw) return;
    this.cellAction.emit({
      action: col.buttonAction || col.key,
      field: col.key,
      rowId: String(row.rowId ?? ''),
      value: row.raw[col.key],
      row: row.raw,
    });
  }
  // ── Action column (a button in every row) ──────────────────────────────────
  /** Open modal state for `rowPopup` / `apiPopup`. Null = closed. */
  actionModal: {
    title: string;
    loading: boolean;
    error: string;
    /** rowPopup: this row's field/value pairs. */
    pairs: Array<{ label: string; value: string }>;
    /** apiPopup: response rows + their column keys. */
    rows: Array<Record<string, unknown>>;
    /** Response columns: `key` indexes the row, `label` is what the header
     *  shows — the flattening prefix is stripped for display. */
    rowColumns: Array<{ key: string; label: string }>;
  } | null = null;
  /** Row ids with an action request in flight, so the button can show a spinner
   *  and refuse double-fires (a double-clicked approve would post twice). */
  actionBusyRows = new Set<string>();

  /** The action modal's root node. Reparented to <body> while open — see
   *  mountActionModal for why. */
  @ViewChild('actionOverlayRoot') private actionOverlayRoot?: ElementRef<HTMLElement>;

  isActionBusy(row: TableModelRow, col: TableModelColumn): boolean {
    return this.actionBusyRows.has(`${col.key}::${row.rowId}`);
  }

  /** The action button's tooltip: the explicit label, else the column label. */
  actionTooltip(col: TableModelColumn): string {
    return col.buttonLabel || col.actionTitle || col.label || 'Action';
  }
  /** A Material icon name (letters/underscores) renders as <mat-icon>; anything
   *  else (an emoji, "→") is drawn as plain text so both styles of config work. */
  isMaterialIcon(icon?: string): boolean {
    return !!icon && /^[a-z][a-z0-9_]*$/i.test(icon.trim());
  }

  /** Action-column button click. Dispatches on `actionKind`; always emits
   *  `cellAction` too, so a host can observe/extend any mode. */
  onActionClick(row: TableModelRow, col: TableModelColumn, event: Event): void {
    event.stopPropagation();
    if (!row.raw || this.isActionBusy(row, col)) return;

    const confirmText = String(col.actionConfirm ?? '').trim();
    if (confirmText && typeof window !== 'undefined' && !window.confirm(confirmText)) return;

    this.cellAction.emit({
      action: col.buttonAction || col.key,
      field: col.key,
      rowId: String(row.rowId ?? ''),
      value: row.raw[col.key],
      row: row.raw,
    });

    switch (col.actionKind) {
      case 'rowPopup':
        this.openRowPopup(row, col);
        return;
      case 'api':
        this.callColumnApi(row, col, false);
        return;
      case 'apiPopup':
        this.openApiPopup(row, col);
        return;
      default:
        // 'emit' (or unset — what the old `button` control did): the emit above
        // IS the behaviour. Nothing further to do.
        return;
    }
  }

  /**
   * `popupComponent`: this row's `[data]` for the embedded <app-dvm-popup>.
   *
   * Takes the popup's baked widgetData and (a) substitutes `{field}` from THIS
   * row throughout its blocks, so one popup definition serves every row, and
   * (b) overrides the trigger so the cell shows the column's own icon/label
   * instead of the popup's original trigger styling.
   *
   * MEMOISED per row+column: this is a template binding, so returning a fresh
   * object every change-detection pass would re-run the popup's ngOnChanges
   * forever (and re-fire its apiTable fetches).
   */
  private rowPopupCache = new Map<string, Record<string, unknown>>();
  rowPopupData(row: TableModelRow, col: TableModelColumn): Record<string, unknown> {
    const key = `${col.key}::${row.rowId}`;
    const cached = this.rowPopupCache.get(key);
    if (cached) return cached;

    const base = (col.popupData ?? {}) as Record<string, unknown>;
    const resolved: Record<string, unknown> = {
      ...this.substituteRowTokens(base, row.raw ?? {}) as Record<string, unknown>,
      // The action column owns the trigger's look — the popup's own trigger
      // config is for a standalone tile, not a table cell.
      triggerMode: col.buttonLabel ? 'button' : 'icon',
      triggerIcon: col.buttonIcon || 'visibility',
      triggerLabel: col.buttonLabel || '',
      triggerHint: this.actionTooltip(col),
      ...(col.actionTitle ? { title: col.actionTitle } : {}),
    };
    this.rowPopupCache.set(key, resolved);
    return resolved;
  }

  /** Deep-walk a popup's widgetData replacing `{field}` in every STRING with
   *  this row's values. Strings only — numbers/booleans/structure are untouched
   *  — so block text, table URLs and request bodies all become row-aware
   *  without the caller enumerating which keys are templatable. */
  private substituteRowTokens(value: unknown, raw: Record<string, unknown>): unknown {
    if (typeof value === 'string') {
      if (!value.includes('{')) return value;
      return value.replace(/\{([^{}"]+)\}/g, (match, field: string) => {
        const resolvedValue = this.readRowField(raw, String(field).trim(), undefined);
        // Leave an unknown token alone rather than blanking it — a literal
        // brace in prose ("{note}") shouldn't silently vanish.
        return resolvedValue === undefined ? match : String(resolvedValue ?? '');
      });
    }
    if (Array.isArray(value)) return value.map((item) => this.substituteRowTokens(item, raw));
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
        out[key] = this.substituteRowTokens(item, raw);
      });
      return out;
    }
    return value;
  }

  /** rowPopup: list this row's VISIBLE columns as label/value pairs. Hidden and
   *  action columns are skipped — one has been deliberately suppressed, the
   *  other has no value. */
  private openRowPopup(row: TableModelRow, col: TableModelColumn): void {
    const pairs = this.model.columns
      .filter((c) => !c.hidden && !c.isAction)
      .map((c) => ({
        label: c.label || c.key,
        value: String(row.cells[c.key]?.display ?? row.cells[c.key]?.value ?? ''),
      }));
    this.actionModal = {
      title: col.actionTitle || col.label || 'Row details',
      loading: false, error: '', pairs, rows: [], rowColumns: [],
    };
    this.mountActionModal();
  }

  /** apiPopup: open the modal in its loading state, then fill it from the call. */
  private openApiPopup(row: TableModelRow, col: TableModelColumn): void {
    this.actionModal = {
      title: col.actionTitle || col.label || 'Details',
      loading: true, error: '', pairs: [], rows: [], rowColumns: [],
    };
    this.mountActionModal();
    this.callColumnApi(row, col, true);
  }

  /** Fire the column's configured request for one row. `intoModal` renders the
   *  response in the open modal; otherwise it's a fire-and-toast action. */
  private callColumnApi(row: TableModelRow, col: TableModelColumn, intoModal: boolean): void {
    const url = String(col.actionUrl ?? '').trim();
    if (!url) {
      const message = 'No action URL configured for this column.';
      if (intoModal && this.actionModal) this.actionModal = { ...this.actionModal, loading: false, error: message };
      else this.uploadMessage = message;
      return;
    }

    const busyKey = `${col.key}::${row.rowId}`;
    this.actionBusyRows.add(busyKey);
    const done = () => this.actionBusyRows.delete(busyKey);

    const resolved = resolveSelfServiceApiUrl(url);
    const method = String(col.actionMethod ?? 'POST').toUpperCase();
    const body = this.buildActionBody(row, col);
    const request$ = method === 'GET'
      ? this.http.get<unknown>(resolved)
      : method === 'DELETE'
        ? this.http.delete<unknown>(resolved)
        : method === 'PUT'
          ? this.http.put<unknown>(resolved, body)
          : this.http.post<unknown>(resolved, body);

    request$.subscribe({
      next: (res) => {
        done();
        if (intoModal) {
          const rows = this.extractActionRows(res, col);
          this.actionModal = this.actionModal
            ? { ...this.actionModal, loading: false, error: '', rows, rowColumns: this.actionRowColumns(rows, col) }
            : null;
        } else {
          this.uploadMessage = `${this.actionTooltip(col)} — done.`;
        }
        // Only for actions that CHANGE the row; a read-only popup must not
        // re-fetch (it would blow away edits in progress elsewhere in the table).
        if (col.actionRefresh) this.refresh.emit();
      },
      error: () => {
        done();
        const message = `${this.actionTooltip(col)} failed — please try again.`;
        if (intoModal && this.actionModal) this.actionModal = { ...this.actionModal, loading: false, error: message };
        else this.uploadMessage = message;
      },
    });
  }

  /** Request body: the configured template with `{field}` placeholders filled
   *  from this row, else the whole row. A template that isn't valid JSON is
   *  sent as-is after substitution, so a non-JSON contract still works. */
  private buildActionBody(row: TableModelRow, col: TableModelColumn): unknown {
    const raw = String(col.actionBody ?? '').trim();
    if (!raw) return row.raw ?? {};
    const filled = this.fillActionTemplate(raw, row.raw ?? {});
    try {
      return JSON.parse(filled);
    } catch {
      // The template isn't valid JSON even after substitution. Send it as-is so
      // a non-JSON contract still works, but say so — this used to fail
      // silently, and the most common cause (a value containing a quote
      // breaking the JSON) is now impossible, so a failure here is a real
      // template error worth surfacing.
      console.warn('[dvm-table] action body is not valid JSON after substitution — sending as text:', filled);
      return filled;
    }
  }

  /**
   * Substitute `{field}` placeholders in an action body template with this
   * row's values, JSON-SAFELY.
   *
   * Two forms, so the author controls the resulting JSON type:
   *   "{rule_name}"   → always a JSON STRING, properly escaped
   *   {premium}       → the value's NATURAL JSON type (number / boolean / null)
   *
   * The quoted form is handled first so the bare pass can't touch it. Values are
   * injected via JSON.stringify rather than raw concatenation: the old version
   * pasted the text in and parsed afterwards, so a value like `He said "hi"`
   * produced invalid JSON and the whole body silently degraded to a string.
   *
   * An unknown field resolves to null (bare) / "" (quoted) rather than being
   * left as a literal `{field}`, which would guarantee a parse failure.
   */
  private fillActionTemplate(template: string, raw: Record<string, unknown>): string {
    const valueOf = (field: string): unknown =>
      this.readRowField(raw, String(field).trim(), undefined);

    return template
      // "{field}" → escaped JSON string
      .replace(/"\{([^{}"]+)\}"/g, (_m, field: string) => {
        const value = valueOf(field);
        return JSON.stringify(value === null || value === undefined ? '' : String(value));
      })
      // {field} → natural JSON type
      .replace(/\{([^{}"]+)\}/g, (_m, field: string) => {
        const value = valueOf(field);
        return JSON.stringify(value === undefined ? null : value);
      });
  }

  /** Response → rows for the popup table. `actionRowsPath` walks a dot-path;
   *  otherwise the first array found in a common envelope wins. A single object
   *  response becomes a one-row table rather than rendering blank. */
  private extractActionRows(response: unknown, col: TableModelColumn): Array<Record<string, unknown>> {
    const rows = this.resolveActionRowArray(response, String(col.actionRowsPath ?? '').trim());
    const flattened = rows.map((row) => this.flattenActionRow(row));
    return this.sortAndCapActionRows(flattened, col);
  }

  /**
   * Find the row array in a response.
   *
   * Tolerates the very common `[{ history: [...] }]` shape — a single-element
   * ARRAY wrapping the object that holds the rows. Without that tolerance a
   * path of `history` resolved to undefined (arrays have no `history` key) and
   * the popup showed "No data", while a blank path returned the wrapper itself
   * as one row whose only column was `history`. Both were reported as the
   * popup "not showing the response".
   */
  private resolveActionRowArray(response: unknown, path: string): Array<Record<string, unknown>> {
    // Step into a 1-element array wrapper at any point in the walk.
    const unwrap = (value: unknown): unknown =>
      (Array.isArray(value) && value.length === 1 && value[0] && typeof value[0] === 'object')
        ? value[0]
        : value;

    if (path) {
      let cursor: unknown = unwrap(response);
      for (const key of path.split('.')) {
        if (cursor == null) break;
        cursor = (unwrap(cursor) as Record<string, unknown>)?.[key];
      }
      if (Array.isArray(cursor)) return cursor as Array<Record<string, unknown>>;
      const record = (cursor && typeof cursor === 'object') ? cursor as Record<string, unknown> : null;
      return record ? [record] : [];
    }

    // No path: the response itself, else the first array under a common
    // envelope key — searched through the array wrapper too.
    if (Array.isArray(response) && response.some((item) => item && typeof item === 'object' && !this.holdsRowArray(item as Record<string, unknown>))) {
      return response as Array<Record<string, unknown>>;
    }
    const record = unwrap(response);
    const asRecord = (record && typeof record === 'object') ? record as Record<string, unknown> : null;
    const found = asRecord ? this.firstRowArrayIn(asRecord) : null;
    if (found) return found;
    if (Array.isArray(response)) return response as Array<Record<string, unknown>>;
    return asRecord ? [asRecord] : [];
  }

  private static readonly ACTION_ROW_KEYS = ['data', 'Data', 'rows', 'results', 'items', 'history', 'changes', 'versions'];

  private holdsRowArray(record: Record<string, unknown>): boolean {
    return !!this.firstRowArrayIn(record);
  }

  private firstRowArrayIn(record: Record<string, unknown>): Array<Record<string, unknown>> | null {
    for (const key of UnifiedTableMatrixChartComponent.ACTION_ROW_KEYS) {
      if (Array.isArray(record[key])) return record[key] as Array<Record<string, unknown>>;
    }
    return null;
  }

  /**
   * Flatten ONE level of nested objects into `Parent.child` columns.
   *
   * An audit/history row typically nests the record under a `DATA` key; left
   * nested, that whole object landed in a single cell as a JSON blob (the
   * table's cell formatter stringifies objects), hiding the values the popup
   * exists to show. Prefixing with the parent key keeps `DATA.updated_by`
   * distinct from a sibling top-level `Updated_by`.
   */
  private flattenActionRow(row: Record<string, unknown>): Record<string, unknown> {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
    const out: Record<string, unknown> = {};
    Object.entries(row).forEach(([key, value]) => {
      const isPlainObject = !!value && typeof value === 'object' && !Array.isArray(value);
      if (isPlainObject) {
        Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
          out[`${key}.${childKey}`] = childValue;
        });
        return;
      }
      out[key] = value;
    });
    return out;
  }

  /** Sort by the configured field then cap the row count — this is what "show
   *  only the latest version" is: sort by the version/date field descending and
   *  keep 1. Absent config leaves the response order untouched. */
  private sortAndCapActionRows(rows: Array<Record<string, unknown>>, col: TableModelColumn): Array<Record<string, unknown>> {
    const sortBy = String(col.actionRowsSortBy ?? '').trim();
    let out = rows;
    if (sortBy) {
      const desc = String(col.actionRowsSortDir ?? 'desc').toLowerCase() !== 'asc';
      // Flattened rows are keyed `Parent.child`, so a bare field name is also
      // matched against the tail of a flattened key ("Version_no" → "DATA.Version_no").
      const keyFor = (row: Record<string, unknown>): string | undefined =>
        Object.prototype.hasOwnProperty.call(row, sortBy)
          ? sortBy
          : Object.keys(row).find((k) => k.split('.').pop() === sortBy);
      out = [...rows].sort((a, b) => {
        const av = a[keyFor(a) ?? sortBy];
        const bv = b[keyFor(b) ?? sortBy];
        const an = Number(av);
        const bn = Number(bv);
        // Numeric when both sides are numeric (version numbers), else a string
        // compare — which sorts ISO timestamps correctly too.
        const cmp = (Number.isFinite(an) && Number.isFinite(bn))
          ? an - bn
          : String(av ?? '').localeCompare(String(bv ?? ''));
        return desc ? -cmp : cmp;
      });
    }
    const max = Number(col.actionMaxRows);
    return Number.isFinite(max) && max > 0 ? out.slice(0, max) : out;
  }

  /**
   * Response columns for the popup table: the row KEY plus the header LABEL.
   *
   * Flattened keys are `Parent.child` (`DATA.comment`), which is what keeps a
   * nested field distinct from a same-named sibling — but showing that prefix in
   * the header is just noise. So the label drops it, UNLESS two columns would
   * then read the same, in which case those keep their qualified name so the
   * header stays unambiguous.
   */
  private actionRowColumns(rows: Array<Record<string, unknown>>, col?: TableModelColumn): Array<{ key: string; label: string }> {
    // Union of keys across all rows — a row with a null nested object would
    // otherwise hide columns that later rows do have.
    const keys: string[] = [];
    rows.forEach((row) => Object.keys(row || {}).forEach((key) => {
      if (!keys.includes(key)) keys.push(key);
    }));

    const leafOf = (key: string) => key.split('.').pop() || key;

    // An explicit column list PICKS and ORDERS the columns shown. Entries match
    // the flattened key exactly ("DATA.comment") or by its short name
    // ("comment"), and `name:Label` overrides the header. Unmatched entries are
    // skipped rather than invented as empty columns.
    const configured = String(col?.actionRowsColumns ?? '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (configured.length) {
      const picked: Array<{ key: string; label: string }> = [];
      configured.forEach((entry) => {
        const [rawName, ...labelParts] = entry.split(':');
        const name = rawName.trim();
        if (!name) return;
        const key = keys.includes(name) ? name : keys.find((k) => leafOf(k) === name);
        if (!key || picked.some((p) => p.key === key)) return;
        const override = labelParts.join(':').trim();
        picked.push({ key, label: override || leafOf(key) });
      });
      if (picked.length) return picked;
      // Nothing matched (a stale list against a changed response) — fall through
      // to every column rather than rendering a header-less empty table.
    }

    const leafCounts = new Map<string, number>();
    keys.forEach((key) => {
      const leaf = leafOf(key);
      leafCounts.set(leaf, (leafCounts.get(leaf) ?? 0) + 1);
    });

    return keys.map((key) => {
      const leaf = leafOf(key);
      return { key, label: (leafCounts.get(leaf) ?? 0) > 1 ? key : leaf };
    });
  }

  /**
   * Move the just-opened modal to <body>.
   *
   * The backdrop is `position: fixed; inset: 0`, which SHOULD cover the
   * viewport — but a fixed element resolves against its nearest TRANSFORMED
   * ancestor, not the viewport, and dashboard tiles carry transforms (hover
   * lift, drag, animations). That turned the tile into the containing block, so
   * the modal was laid out inside the tile AND clipped by its `overflow`,
   * appearing squashed under the table instead of over the page.
   *
   * Reparenting the node is the same fix DvmPopupComponent uses. Angular's
   * bindings and listeners keep working on the moved node, and closing (the
   * *ngIf) removes it from wherever it lives.
   */
  private mountActionModal(): void {
    setTimeout(() => {
      const el = this.actionOverlayRoot?.nativeElement;
      if (el && el.parentElement !== document.body) document.body.appendChild(el);
    });
  }

  closeActionModal(): void {
    this.actionModal = null;
  }

  /** Cell text for the popup table (same tolerance as the main table). */
  actionCellText(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  /** Resolve a hyperlink cell's href (template `{value}` substitution). */
  cellHref(row: TableModelRow, col: TableModelColumn): string {
    const v = String(row.raw?.[col.key] ?? '');
    return col.hrefTemplate ? col.hrefTemplate.replace(/\{value\}/g, encodeURIComponent(v)) : v;
  }
  /** Multiselect: the cell value as a string[] for the multi <select> ngModel. */
  cellMultiValue(row: TableModelRow, key: string): string[] {
    const v = row.cells[key]?.value;
    if (Array.isArray(v)) return v.map(String);
    return v === undefined || v === null || v === '' ? [] : String(v).split(',').map((s) => s.trim()).filter(Boolean);
  }
  /** File cell control: base64-encode the picked file into the cell value
   *  (reuses the JSON-safe upload shape the file-upload widget uses). */
  onCellFile(row: TableModelRow, key: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => this.onCellEdit(row, key, { name: file.name, type: file.type, content: String(reader.result || '') });
    reader.readAsDataURL(file);
  }
  // ── Lookup / Autocomplete options (config list + optional live fetch) ────────
  private lookupCache: Record<string, Array<{ value: string; label: string }>> = {};
  private lookupInFlight = new Set<string>();
  /** Options for a lookup/autocomplete datalist: fetched cache else config options. */
  lookupOptions(col: TableModelColumn): Array<{ value: string; label: string }> {
    return this.lookupCache[col.key] || col.options || [];
  }
  /** Fetch options from the column's lookupUrl once (on focus). */
  ensureLookupOptions(col: TableModelColumn, q = ''): void {
    if (!col.lookupUrl || this.lookupInFlight.has(col.key)) return;
    this.lookupInFlight.add(col.key);
    const baseUrl = this.resolveTableApiUrl(col.lookupUrl);
    const sep = baseUrl.includes('?') ? '&' : '?';
    const url = `${baseUrl}${sep}field=${encodeURIComponent(col.key)}&q=${encodeURIComponent(q)}`;
    this.http.get<Array<{ value: string; label?: string }>>(url).subscribe({
      next: (res) => {
        this.lookupInFlight.delete(col.key);
        this.lookupCache = { ...this.lookupCache, [col.key]: (res || []).map((o) => ({ value: String(o.value), label: String(o.label ?? o.value) })) };
      },
      error: () => this.lookupInFlight.delete(col.key),
    });
  }
  /** Autocomplete keystroke → refetch matching options from the server. */
  onLookupInput(col: TableModelColumn, event: Event): void {
    if (!col.lookupUrl) return;
    const q = (event.target as HTMLInputElement)?.value || '';
    this.lookupInFlight.delete(col.key); // allow the refetch
    this.ensureLookupOptions(col, q);
  }

  // ── Editable — auto-save (debounced) ────────────────────────────────────────
  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private scheduleAutoSave(row: TableModelRow): void {
    if (!this.enableAutoSave) return;
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    const ms = Number(this.capabilities?.autoSaveDebounceMs ?? 1200);
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      // Auto-save the row only when it's valid (don't push invalid data).
      if (this.enableValidation) { this.validateRow(row); if (row.rowId && this.rowErrors[row.rowId]) return; }
      this.saveRow(row);
    }, Math.max(200, ms));
  }

  // ── Editable — row operations (all client-side) ─────────────────────────────
  /** Build a blank row (every column key present, empty) marked 'new'. */
  private blankRow(): Record<string, unknown> {
    const r: Record<string, unknown> = {};
    // Action columns hold a button, not a value — seeding a key for them would
    // send a phantom field to the backend on every insert.
    this.model.columns.forEach((c) => {
      if (c.isAction) return;
      r[c.key] = c.control === 'checkbox' ? false : '';
    });
    r['__rowId'] = `new-${this.newRowSeq++}`;
    r['__state'] = 'new';
    r['__dirtyColumns'] = [];
    return r;
  }
  /** Append a new blank row at the end. */
  addRow(): void {
    this.pushUndo();
    this.lastRows = [...this.lastRows, this.blankRow()];
    this.rebuildEditable();
  }
  /** Insert a blank row above/below the given model row. */
  insertRow(row: TableModelRow, where: 'above' | 'below'): void {
    if (!row.raw) return;
    const idx = this.lastRows.indexOf(row.raw);
    if (idx < 0) return;
    this.pushUndo();
    const at = where === 'above' ? idx : idx + 1;
    const next = [...this.lastRows];
    next.splice(at, 0, this.blankRow());
    this.lastRows = next;
    this.rebuildEditable();
  }
  /** Delete a row — soft (mark 'deleted', keep) or hard (remove). */
  deleteRow(row: TableModelRow): void {
    if (!row.raw) return;
    this.pushUndo();
    if (this.enableSoftDelete) {
      // Toggle soft-delete: a new row is removed outright, others are flagged.
      if (row.raw['__state'] === 'new') this.lastRows = this.lastRows.filter((r) => r !== row.raw);
      else row.raw['__state'] = row.raw['__state'] === 'deleted' ? 'modified' : 'deleted';
    } else {
      this.lastRows = this.lastRows.filter((r) => r !== row.raw);
    }
    if (row.rowId) delete this.rowErrors[row.rowId];
    this.rebuildEditable();
  }
  /** Restore a soft-deleted row. */
  restoreRow(row: TableModelRow): void {
    if (!row.raw || row.raw['__state'] !== 'deleted') return;
    this.pushUndo();
    row.raw['__state'] = 'modified';
    row.raw['__dirtyColumns'] = Array.isArray(row.raw['__dirtyColumns']) ? row.raw['__dirtyColumns'] : [];
    this.rebuildEditable();
  }
  /** Duplicate a row directly below it (as a new row). */
  duplicateRow(row: TableModelRow): void {
    if (!row.raw) return;
    const idx = this.lastRows.indexOf(row.raw);
    if (idx < 0) return;
    this.pushUndo();
    const copy = { ...row.raw, __rowId: `new-${this.newRowSeq++}`, __state: 'new', __dirtyColumns: [] as string[] };
    const next = [...this.lastRows];
    next.splice(idx + 1, 0, copy);
    this.lastRows = next;
    this.rebuildEditable();
  }
  copyRow(row: TableModelRow): void { if (row.raw) this.rowClipboard = { ...row.raw }; }
  /** Paste the clipboard row's values into an existing row (marks it modified). */
  pasteRow(row: TableModelRow): void {
    if (!row.raw || !this.rowClipboard) return;
    this.pushUndo();
    const preserve = { __rowId: row.raw['__rowId'], __state: row.raw['__state'] === 'new' ? 'new' : 'modified' };
    Object.keys(this.rowClipboard).forEach((k) => { if (k !== '__rowId' && k !== '__state') row.raw![k] = this.rowClipboard![k]; });
    Object.assign(row.raw, preserve);
    row.raw['__dirtyColumns'] = Object.keys(this.rowClipboard).filter((k) => k !== '__rowId' && k !== '__state');
    this.rebuildEditable();
  }

  // ── Editable — undo / redo (row snapshots) ──────────────────────────────────
  private snapshot(): Array<Record<string, unknown>> { return this.lastRows.map((r) => ({ ...r })); }
  private pushUndo(): void {
    if (!this.enableUndoRedo && !this.isEditable) return;
    this.undoStack = [...this.undoStack, this.snapshot()].slice(-50);
    this.redoStack = [];
  }
  undo(): void {
    if (!this.undoStack.length) return;
    this.redoStack = [...this.redoStack, this.snapshot()];
    const prev = this.undoStack[this.undoStack.length - 1];
    this.undoStack = this.undoStack.slice(0, -1);
    this.lastRows = prev;
    this.rebuildEditable();
    this.revalidateAll();
  }
  redo(): void {
    if (!this.redoStack.length) return;
    this.undoStack = [...this.undoStack, this.snapshot()];
    const next = this.redoStack[this.redoStack.length - 1];
    this.redoStack = this.redoStack.slice(0, -1);
    this.lastRows = next;
    this.rebuildEditable();
    this.revalidateAll();
  }

  // ── Editable — cancel / validate / save ─────────────────────────────────────
  /** Discard all edits, restoring the pristine snapshot. */
  cancelChanges(): void {
    this.lastRows = this.originalRows.map((r) => ({ ...r }));
    this.undoStack = [];
    this.redoStack = [];
    this.rowErrors = {};
    this.rebuildEditable();
  }
  /** Validate a single row against its columns' rules → rowErrors[rowId]. */
  private validateRow(row: TableModelRow): void {
    if (!row.raw || !row.rowId) return;
    const errors: Record<string, string> = {};
    this.model.columns.forEach((col) => {
      // A hidden column has no cell to show an error against and no input to fix
      // it in, so validating it would block Save with an uncorrectable message.
      // Its value still goes out in the payload — the backend owns its rules.
      if (col.hidden || col.isAction) return;
      // Field-or-label tolerant, same as toSaveRow — otherwise a column whose
      // data sits under its display name reads as empty and trips `required`.
      const value = this.readRowField(row.raw!, col.key, col.label);
      const err = this.validateValue(value, col, row);
      if (err) errors[col.key] = err;
    });
    if (Object.keys(errors).length) this.rowErrors = { ...this.rowErrors, [row.rowId]: errors };
    else { const next = { ...this.rowErrors }; delete next[row.rowId]; this.rowErrors = next; }
    row.errors = errors;
  }
  /** One value against one column's rules — returns the first message or ''. */
  private validateValue(value: unknown, col: TableModelColumn, row: TableModelRow): string {
    const s = value === null || value === undefined ? '' : String(value);
    // CONDITIONAL required: only enforced when another column equals a value.
    const condActive = col.requiredWhen
      ? String(row.raw?.[col.requiredWhen.field] ?? '') === String(col.requiredWhen.equals ?? '')
      : false;
    if ((col.required || condActive) && s.trim() === '') return `${col.label} is required`;
    if (s === '') return '';
    // Numeric validation applies ONLY when the column's EDITING CONTROL is
    // explicitly numeric — never from the inferred display `type`. A column may
    // display as a number yet hold free-form text (e.g. codes like "LC0570500"),
    // so the inferred type must not force a "must be a number" rule.
    const isNumericControl = col.control === 'number' || col.control === 'currency' || col.control === 'percent';
    if (isNumericControl) {
      const n = Number(s);
      if (!Number.isFinite(n)) return `${col.label} must be a number`;
      if (col.min != null && n < col.min) return `${col.label} ≥ ${col.min}`;
      if (col.max != null && n > col.max) return `${col.label} ≤ ${col.max}`;
    }
    if (col.regex) {
      try { if (!new RegExp(col.regex).test(s)) return `${col.label} format invalid`; } catch { /* bad regex → skip */ }
    }
    if (col.unique) {
      const dupe = this.lastRows.some((r) => r !== row.raw && r['__state'] !== 'deleted' && String(r[col.key] ?? '') === s);
      if (dupe) return `${col.label} must be unique`;
    }
    // CROSS-FIELD: this value must be ≥ / ≤ another column's value.
    if (col.gteField && row.raw) {
      const other = Number(row.raw[col.gteField]); const self = Number(s);
      if (Number.isFinite(other) && Number.isFinite(self) && self < other) return `${col.label} must be ≥ ${col.gteField}`;
    }
    if (col.lteField && row.raw) {
      const other = Number(row.raw[col.lteField]); const self = Number(s);
      if (Number.isFinite(other) && Number.isFinite(self) && self > other) return `${col.label} must be ≤ ${col.lteField}`;
    }
    return '';
  }
  /** Re-validate every data row (after undo/redo/paste). */
  private revalidateAll(): void {
    this.rowErrors = {};
    if (!this.enableValidation) return;
    this.model.rows.filter((r) => r.kind === 'data').forEach((r) => this.validateRow(r));
  }
  /** Loose key match so 'rule_name' finds a row keyed 'Rule Name'. */
  private normalizeRowKey(key: string): string {
    return key.toLowerCase().replace(/[^a-z0-9]/g, '');
  }
  /** Read one field off a working row. A response is often keyed by DISPLAY
   *  NAMES ("Rule Name") while columns are configured with snake_case fields
   *  ("rule_name"), so resolve field → label → normalized match — the same
   *  field-or-label tolerance the RENDERER uses (resolveRowValue), which the
   *  save path previously lacked. `onCellEdit` writes under the column KEY, so
   *  an exact key hit comes first and an edit always wins. */
  private readRowField(raw: Record<string, unknown>, key: string, label?: string): unknown {
    const has = (k?: string) => !!k && Object.prototype.hasOwnProperty.call(raw, k);
    if (has(key)) return raw[key];
    if (has(label)) return raw[label as string];
    const target = this.normalizeRowKey(key);
    const match = Object.keys(raw).find((k) => !k.startsWith('__') && this.normalizeRowKey(k) === target);
    return match ? raw[match] : undefined;
  }
  /** Serialize one working row into the save-payload shape (values + version).
   *  Keys stay the CONFIGURED field names; only the value lookup is tolerant. */
  private toSaveRow(r: Record<string, unknown>): TableMatrixSaveRow {
    const values: Record<string, unknown> = {};
    // Skip action columns: they have no dataset field, so including them would
    // add a key the backend never asked for (and always null).
    this.model.columns.forEach((c) => {
      if (c.isAction) return;
      values[c.key] = this.readRowField(r, c.key, c.label);
    });
    const vf = this.capabilities?.versionField;
    if (this.capabilities?.enableOptimisticLock && vf && r[vf] != null) values[vf] = r[vf];
    return { rowId: String(r['__rowId'] ?? ''), state: r['__state'] as TableMatrixSaveRow['state'], values };
  }
  /** The custom body configured for ONE endpoint, or undefined to POST the
   *  default contract. Keyed by the endpoint's config key so each URL keeps its
   *  own shape. */
  private payloadTemplateFor(key: TableMatrixEndpointKey): TableMatrixPayloadTemplate | undefined {
    return this.capabilities?.payloadTemplates?.[key];
  }
  /** Read an explicit field list straight off the working row. Lets a payload
   *  carry fields the rendered column set doesn't cover — e.g. a Matrix's row
   *  dimensions, which live on the source row but never become model columns.
   *  Internal bookkeeping keys (`__rowId`/`__state`) are never sent. */
  private pickRowFields(raw: Record<string, unknown>, fields: string[]): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    fields.forEach((field) => {
      const key = String(field ?? '').trim();
      if (!key || key.startsWith('__')) return;
      // Same field-or-label tolerance as toSaveRow — a picked column names the
      // CONFIGURED field, while the row may be keyed by its display name.
      const label = this.model.columns.find((c) => c.key === key)?.label;
      const value = this.readRowField(raw, key, label);
      // `?? null` on purpose: JSON.stringify DROPS undefined values, so a field
      // the row doesn't carry would vanish from the payload with no trace — the
      // explicit null makes a mis-typed/missing field visible instead.
      values[key] = value ?? null;
    });
    return values;
  }
  /** Build a custom request body for ONE row from that endpoint's template
   *  (e.g. { requestData: {...}, endpoints: [...], entity, workflowId, version }
   *  instead of the default { componentId, dataset, operation, rows } shape).
   *  The row's own values are the dynamic part and always win on key overlap. */
  private buildCustomPayload(
    row: TableMatrixSaveRow,
    raw: Record<string, unknown>,
    tpl: TableMatrixPayloadTemplate,
  ): Record<string, unknown> {
    const dataKey = tpl.dataKey || 'requestData';
    // A configured column list wins; otherwise send every column of the table
    // (toSaveRow's full model set — hidden columns included).
    // A configured list is exact - an edited column missing from it would be
    // silently dropped and the backend would 200 having changed nothing. So
    // the row's own dirty columns always ride along.
    const dirtyKeys = Array.isArray(raw['__dirtyColumns']) ? (raw['__dirtyColumns'] as unknown[]).map(String) : [];
    const fields = tpl.includeColumns?.length ? Array.from(new Set([...tpl.includeColumns, ...dirtyKeys])) : null;
    const values = fields ? this.pickRowFields(raw, fields) : row.values;
    return {
      // Static values + extra top-level fields go through token substitution, so
      // a configured `{userId}` / `{now}` resolves instead of being POSTed as
      // the literal text "{userId}". Row values are NOT substituted — they are
      // the data.
      [dataKey]: { ...this.resolvePayloadTokens(tpl.staticValues, raw) as Record<string, unknown>, ...values },
      endpoints: tpl.endpoints ?? [],
      ...this.resolvePayloadTokens(tpl.extraFields, raw) as Record<string, unknown>,
    };
  }

  /* ── Payload template tokens ──────────────────────────────────────────────
   * The save/bulk-save body builder ("Request body" modal) lets an author add
   * static values and extra top-level fields. Those were emitted verbatim, so a
   * field like `userId: {userId}` shipped the literal braces to the backend.
   *
   * These tokens now resolve:
   *   {userId} {userEmail} {userName}  — the signed-in user (Keycloak profile)
   *   {now} / {timestamp}              — ISO-8601 of the request
   *   {componentId} {dataset}          — this table's own identity
   *   {<column>}                       — that row's value, on a per-row save
   * ------------------------------------------------------------------------ */

  /** Signed-in user, resolved once on init so substitution stays synchronous
   *  (the save path builds its body inline and can't await). */
  private userContext: { userId: string; userEmail: string; userName: string } = { userId: '', userEmail: '', userName: '' };

  private async resolveUserContext(): Promise<void> {
    try {
      if (!this.keycloak || !(await this.keycloak.isLoggedIn())) return;
      const profile = await this.keycloak.loadUserProfile();
      this.userContext = {
        // `id` is the Keycloak subject; username/email are the usual fallbacks
        // a backend accepts as a user identifier.
        userId: String((profile as { id?: string }).id ?? profile.username ?? profile.email ?? ''),
        userEmail: String(profile.email ?? ''),
        userName: String(profile.username ?? profile.email ?? ''),
      };
    } catch { /* unauthenticated / no Keycloak — tokens resolve to '' */ }
  }

  /** The non-row tokens available to a payload template. */
  private payloadTokenContext(): Record<string, string> {
    const now = new Date().toISOString();
    return {
      userId: this.userContext.userId,
      userEmail: this.userContext.userEmail,
      userName: this.userContext.userName,
      now,
      timestamp: now,
      componentId: String(this.capabilities?.componentId ?? ''),
      dataset: String(this.capabilities?.dataset ?? ''),
    };
  }

  /**
   * Deep-substitute `{token}` in every STRING of a template fragment.
   *
   * A value that is EXACTLY one token keeps the resolved value's own type (so
   * `{now}` stays a string but a numeric row field stays a number); a token
   * embedded in surrounding text interpolates into that string. An unknown
   * token is left as-is rather than blanked, so a literal brace in configured
   * text doesn't silently vanish.
   */
  private resolvePayloadTokens(value: unknown, raw?: Record<string, unknown>): unknown {
    if (value === null || value === undefined) return value;

    if (typeof value === 'string') {
      if (!value.includes('{')) return value;
      const context = this.payloadTokenContext();
      const lookup = (name: string): unknown => {
        const key = name.trim();
        if (Object.prototype.hasOwnProperty.call(context, key)) return context[key];
        // Row fields only apply to a per-row save; a bulk save has no single row.
        return raw ? this.readRowField(raw, key, undefined) : undefined;
      };
      const whole = value.match(/^\{([^{}]+)\}$/);
      if (whole) {
        const resolved = lookup(whole[1]);
        return resolved === undefined ? value : resolved;   // keep the real type
      }
      return value.replace(/\{([^{}]+)\}/g, (match, name: string) => {
        const resolved = lookup(name);
        return resolved === undefined ? match : String(resolved ?? '');
      });
    }

    if (Array.isArray(value)) return value.map((item) => this.resolvePayloadTokens(item, raw));
    if (typeof value === 'object') {
      const out: Record<string, unknown> = {};
      Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
        out[key] = this.resolvePayloadTokens(item, raw);
      });
      return out;
    }
    return value;
  }
  private saveState = new Set<string>(); // rowIds mid-save (spinner / disable)
  /** POSTs still outstanding in the current save batch + whether any landed -
   *  `saved` fires once when the counter reaches zero with a success. */
  private inFlightSaves = 0;
  private batchHadSuccess = false;
  /** Edits the server acknowledged but may not serve back yet (async commit /
   *  hydration lag on the re-fetch). `identity` = the row's NON-edited values
   *  (how we find the same row in a fresh payload), `overlay` = the saved
   *  edited values re-applied until the server serves them itself. */
  private pendingSaves: Array<{ identity: Record<string, unknown>; overlay: Record<string, unknown>; at: number }> = [];
  private static readonly PENDING_SAVE_TTL_MS = 60_000;

  private beginSaveBatch(count: number): void {
    this.inFlightSaves = Math.max(0, count);
    this.batchHadSuccess = false;
  }
  private settleSave(success: boolean): void {
    if (success) this.batchHadSuccess = true;
    this.inFlightSaves = Math.max(0, this.inFlightSaves - 1);
    if (this.inFlightSaves > 0) return;
    const emit = this.batchHadSuccess;
    this.batchHadSuccess = false;
    if (emit) this.saved.emit();
  }
  /** Remember a just-saved row's edits so a stale re-fetch can't visibly revert
   *  them. `dirtyKeys` are the raw-row keys the user edited. */
  private rememberPendingSave(raw: Record<string, unknown>, dirtyKeys: string[]): void {
    if (!dirtyKeys.length) return;
    const identity: Record<string, unknown> = {};
    const overlay: Record<string, unknown> = {};
    Object.keys(raw).forEach((k) => {
      if (k.startsWith('__')) return;
      if (dirtyKeys.includes(k)) overlay[k] = raw[k];
      else identity[k] = raw[k];
    });
    if (!Object.keys(overlay).length) return;
    this.pendingSaves.push({ identity, overlay, at: Date.now() });
  }
  /** Re-apply acknowledged-but-not-yet-served edits onto incoming rows. An
   *  overlay retires as soon as the server serves the same values (or on TTL).
   *  Identity matches on the row's OTHER columns, so a server-derived column
   *  that changed because of this very edit (e.g. a realized cost recomputed
   *  from the edited percent) is tolerated: those keys are skipped when the
   *  strict match finds nothing. */
  private overlayPendingSaves(rows: Array<Record<string, unknown>>): void {
    if (!this.pendingSaves.length) return;
    const now = Date.now();
    const same = (a: unknown, b: unknown) => String(a ?? '') === String(b ?? '');
    const textKeys = (identity: Record<string, unknown>) =>
      Object.keys(identity).filter((k) => typeof identity[k] === 'string' && Number.isNaN(Number(identity[k])));
    this.pendingSaves = this.pendingSaves.filter((p) => {
      if (now - p.at > UnifiedTableMatrixChartComponent.PENDING_SAVE_TTL_MS) return false;
      const idKeys = Object.keys(p.identity);
      let hit = rows.find((r) => idKeys.every((k) => same(r[k], p.identity[k])));
      if (!hit) {
        // Relaxed match: the non-numeric identity columns (labels / keys) -
        // numeric siblings may legitimately have been recomputed server-side.
        const keys = textKeys(p.identity);
        if (keys.length) {
          const cands = rows.filter((r) => keys.every((k) => same(r[k], p.identity[k])));
          if (cands.length === 1) hit = cands[0];
        }
      }
      if (!hit) return true; // row not in this payload (filtered out) - keep waiting
      const served = Object.keys(p.overlay).every((k) => same(hit![k], p.overlay[k]));
      if (served) return false; // server caught up - overlay no longer needed
      Object.assign(hit, p.overlay);
      return true;
    });
  }
  isSaving(row: TableModelRow): boolean { return !!row.rowId && this.saveState.has(row.rowId); }
  private setStatusMessage(message: string, tone: 'info' | 'success' | 'error' = 'info'): void {
    this.uploadMessage = message;
    this.uploadMessageTone = tone;
  }
  private showSaveNotice(message: string, tone: 'success' | 'error'): void {
    if (this.saveNoticeTimer) clearTimeout(this.saveNoticeTimer);
    this.saveNotice = message;
    this.saveNoticeTone = tone;
    this.saveNoticeTimer = setTimeout(() => {
      this.saveNotice = '';
      this.saveNoticeTimer = null;
    }, 3000);
  }

  /** Save ALL dirty rows (bulk). POSTs to the configured bulk endpoint when set
   *  (as ONE transaction when `transactional`), else emits for the parent. */
  saveAllRows(): void {
    if (this.enableValidation) {
      this.revalidateAll();
      if (this.errorRowCount > 0) return;
    }
    const dirty = this.lastRows.filter((r) => { const s = r['__state']; return s === 'new' || s === 'modified' || s === 'deleted'; });
    if (!dirty.length) return;
    const rows = dirty.map((r) => this.toSaveRow(r));
    const cap = this.capabilities;
    // Which endpoint this bulk save lands on decides whose custom body applies.
    const urlKey: TableMatrixEndpointKey = cap?.bulkSaveUrl ? 'bulkSaveUrl' : 'saveUrl';
    const url = this.resolveTableApiUrl((cap?.bulkSaveUrl || cap?.saveUrl || '').trim());
    const payload: TableMatrixSaveEvent = {
      componentId: cap?.componentId, dataset: cap?.dataset, saveUrl: url || undefined,
      operation: 'bulkSave', rows, transactional: cap?.transactional === true,
    };
    if (!url) {
      this.save.emit(payload);
      // No endpoint on the component → the HOST owns persistence (builder
      // toast / published default-endpoint POST). Optimistically settle the
      // local change state (mirrors postSave's success reconciliation) so the
      // Save all/Cancel pair hides; a host-side failure surfaces via its own
      // error toast and the next refresh restores server truth.
      this.lastRows = this.lastRows.filter((r) => r['__state'] !== 'deleted');
      this.lastRows.forEach((r) => {
        if (r['__state'] === 'new' || r['__state'] === 'modified') r['__state'] = 'saved';
        r['__dirtyColumns'] = [];
      });
      this.rowErrors = {};
      this.rebuildEditable();
      this.setStatusMessage('Saved', 'success');
      return;
    }
    const tpl = this.payloadTemplateFor(urlKey);
    if (tpl) {
      // Custom per-record contract has no bulk-array shape: one POST per row.
      this.beginSaveBatch(dirty.length);
      dirty.forEach((raw, i) => this.postSave(url, this.buildCustomPayload(rows[i], raw, tpl), [raw]));
      return;
    }
    this.beginSaveBatch(1);
    this.postSave(url, payload, dirty);
  }

  /** Save a SINGLE row (per-row Save button / auto-save / save-on-exit). Routes
   *  to create/update/delete by the row's state. */
  saveRow(row: TableModelRow): void {
    if (!row.raw) return;
    const state = row.raw['__state'];
    if (state !== 'new' && state !== 'modified' && state !== 'deleted') return;
    if (this.enableValidation) { this.validateRow(row); if (row.rowId && this.rowErrors[row.rowId]) return; }
    const cap = this.capabilities;
    const opMap: Record<string, 'create' | 'update' | 'delete'> = { new: 'create', modified: 'update', deleted: 'delete' };
    const operation = opMap[String(state)];
    const keyMap: Record<string, TableMatrixEndpointKey> = { create: 'createUrl', update: 'updateUrl', delete: 'deleteUrl' };
    const urlMap: Record<string, string | undefined> = { create: cap?.createUrl, update: cap?.updateUrl, delete: cap?.deleteUrl };
    // The per-operation URL when set, else the shared saveUrl fallback — and the
    // custom body follows whichever endpoint actually gets the POST.
    const urlKey: TableMatrixEndpointKey = urlMap[operation] ? keyMap[operation] : 'saveUrl';
    const url = this.resolveTableApiUrl((urlMap[operation] || cap?.saveUrl || '').trim());
    const saveRow = this.toSaveRow(row.raw);
    const payload: TableMatrixSaveEvent = {
      componentId: cap?.componentId, dataset: cap?.dataset, saveUrl: url || undefined,
      operation, rows: [saveRow],
    };
    if (!url) {
      this.save.emit(payload);
      // Host owns persistence — settle this row locally (same as bulk save).
      if (row.raw['__state'] === 'deleted') this.lastRows = this.lastRows.filter((r) => r !== row.raw);
      else {
        row.raw['__state'] = 'saved';
        row.raw['__dirtyColumns'] = [];
      }
      if (row.rowId) delete this.rowErrors[row.rowId];
      this.rebuildEditable();
      this.setStatusMessage('Saved', 'success');
      return;
    }
    const tpl = this.payloadTemplateFor(urlKey);
    this.beginSaveBatch(1);
    this.postSave(url, tpl ? this.buildCustomPayload(saveRow, row.raw, tpl) : payload, [row.raw]);
  }

  /** POST a save payload and reconcile row states on the response. On success
   *  rows flip to 'saved' (deleted rows drop out). On failure they flip to
   *  'failed' and the ORIGINAL values roll back (client-side rollback). A 409
   *  is surfaced as an optimistic-lock conflict. */
  private postSave(url: string, payload: unknown, rawRows: Array<Record<string, unknown>>): void {
    const resolvedUrl = this.resolveTableApiUrl(url);
    const ids = rawRows.map((r) => String(r['__rowId'] ?? '')).filter(Boolean);
    ids.forEach((id) => this.saveState.add(id));
    // Snapshot for rollback-on-failure.
    const snapshot = rawRows.map((r) => ({ ...r }));
    this.http.post<{ results?: Array<{ rowId: string; state?: string; version?: unknown; errors?: Array<{ field: string; message: string }> }> }>(resolvedUrl, payload).subscribe({
      next: (res) => {
        ids.forEach((id) => this.saveState.delete(id));
        const results = Array.isArray(res?.results) ? res!.results! : null;
        let hasErrors = false;
        rawRows.forEach((r) => {
          const rowId = String(r['__rowId'] ?? '');
          const result = results?.find((x) => String(x.rowId) === rowId);
          const vf = this.capabilities?.versionField;
          if (result?.errors?.length) { r['__state'] = 'failed'; hasErrors = true; return; }
          if (r['__state'] === 'deleted') { this.lastRows = this.lastRows.filter((x) => x !== r); return; }
          if (vf && result?.version != null) r[vf] = result.version; // version bump
          // Keep the edit visible across a stale re-fetch (see pendingSaves).
          this.rememberPendingSave(r, Array.isArray(r['__dirtyColumns']) ? (r['__dirtyColumns'] as unknown[]).map(String) : []);
          r['__state'] = 'saved';
          r['__dirtyColumns'] = [];
        });
        this.rebuildEditable();
        this.setStatusMessage(hasErrors ? 'Not saved' : 'Saved', hasErrors ? 'error' : 'success');
        this.showSaveNotice(hasErrors ? 'Not saved' : 'Saved', hasErrors ? 'error' : 'success');
        this.settleSave(!hasErrors);
      },
      error: (err) => {
        ids.forEach((id) => this.saveState.delete(id));
        this.settleSave(false);
        // Rollback-on-failure: restore each row's pre-save values + mark failed.
        rawRows.forEach((r, i) => { Object.assign(r, snapshot[i]); r['__state'] = 'failed'; });
        if (err?.status === 409) this.uploadMessage = 'Save conflict — the record changed on the server. Refresh and retry.';
        if (err?.status !== 409) this.setStatusMessage('Not saved', 'error');
        this.rebuildEditable();
        this.showSaveNotice(err?.status === 409 ? 'Not saved - conflict' : 'Not saved', 'error');
      },
    });
  }

  // ── Workflow (config-driven; POSTs to workflowUrl or emits) ─────────────────
  /** IDs of the currently-selected data rows (for a workflow action target). */
  private selectedRowIds(): string[] {
    const paged = this.pagedRows;
    const ids: string[] = [];
    this.selectedRows.forEach((idx) => { const r = paged[idx]; if (r?.raw?.['__rowId']) ids.push(String(r.raw['__rowId'])); });
    return ids;
  }
  runWorkflow(action: string): void {
    const rowIds = this.selectedRowIds();
    if (!rowIds.length) { this.uploadMessage = 'Select one or more rows first.'; return; }
    const cap = this.capabilities;
    const url = this.resolveTableApiUrl((cap?.workflowUrl || '').trim());
    const payload: TableMatrixWorkflowEvent = {
      componentId: cap?.componentId, dataset: cap?.dataset, workflowUrl: url || undefined, action, rowIds,
    };
    this.workflow.emit(payload);
    if (url) {
      this.http.post(url, payload).subscribe({
        next: () => { this.uploadMessage = `Workflow: ${action} submitted.`; this.refresh.emit(); },
        error: () => { this.uploadMessage = `Workflow ${action} failed.`; },
      });
    }
  }
  /** Pretty label for a workflow action button. */
  workflowLabel(action: string): string { return action.charAt(0).toUpperCase() + action.slice(1); }

  // ── Export CSV (client-side) ───────────────────────────────────────────────
  exportCsv(): void {
    const cols = this.visibleColumns;
    const header = cols.map((c) => `"${(c.columnGroup ? `${c.columnGroup} ${c.label || c.key}` : (c.label || c.key)).replace(/"/g, '""')}"`).join(',');
    const lines = this.processedRows.filter((r) => r.kind === 'data').map((r) =>
      cols.map((c) => `"${String(r.cells[c.key]?.display ?? '').replace(/"/g, '""')}"`).join(','),
    );
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.cfg.title || 'table'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  toggleFullScreen(): void { this.fullScreen = !this.fullScreen; }

  /** The 2D matrix of [header row, ...data rows] for Excel/PDF export. */
  private exportMatrix(): { header: string[]; rows: string[][] } {
    const cols = this.visibleColumns;
    // Grouped (matrix/pivot) columns carry only the column value as label —
    // prefix the group (measure) name so the exported header is unambiguous.
    const header = cols.map((c) => (c.columnGroup ? `${c.columnGroup} ${c.label || c.key}` : (c.label || c.key)));
    const rows = this.processedRows
      .filter((r) => r.kind === 'data' || r.kind === 'subtotal' || r.kind === 'grandtotal' || r.kind === 'total')
      .map((r) => cols.map((c) => String(r.cells[c.key]?.display ?? '')));
    return { header, rows };
  }
  private exportName(ext: string): string { return `${this.cfg.title || 'table'}.${ext}`; }

  /** Export to Excel using the already-installed SheetJS (xlsx) + file-saver. */
  async exportExcel(): Promise<void> {
    const { header, rows } = this.exportMatrix();
    // Dynamic import so xlsx only loads when the user actually exports.
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, this.exportName('xlsx'));
  }

  /** Export to PDF — a clean printable HTML table opened in a new window's
   *  print-to-PDF (no extra dependency). */
  exportPdf(): void { this.printTable(true); }

  /** Print (or print-to-PDF) a clean, styled copy of the visible table. */
  printTable(asPdf = false): void {
    const { header, rows } = this.exportMatrix();
    const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const thead = `<tr>${header.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`;
    const tbody = rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('');
    const title = esc(String(this.cfg.title || 'Table'));
    const html = `<!doctype html><html><head><title>${title}</title><style>
      body{font-family:Segoe UI,Arial,sans-serif;margin:16px;color:#172033}
      h2{margin:0 0 12px}
      table{border-collapse:collapse;width:100%;font-size:12px}
      th,td{border:1px solid #cbd5e1;padding:5px 8px;text-align:left}
      th{background:#f1f5f9;font-weight:700}
      tr:nth-child(even) td{background:#f8fafc}
      @media print{@page{margin:12mm}}
    </style></head><body><h2>${title}</h2><table><thead>${thead}</thead><tbody>${tbody}</tbody></table>
    <script>window.onload=function(){window.print();${asPdf ? '' : 'setTimeout(function(){window.close()},300);'}}<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
  }

  /** Emit a refresh request to the parent (re-fetch/re-compute). */
  onRefresh(): void { this.refresh.emit(); }

  // ── Save / restore user preferences (localStorage) ──────────────────────────
  private get prefsKey(): string {
    const id = String(this.capabilities?.componentId || this.cfg.widgetId || 'tm');
    return `dvm.tm.prefs.${id}`;
  }
  saveUserPrefs(): void {
    if (!this.enableSavePrefs) return;
    const prefs = {
      columnOrder: this.columnOrder,
      columnWidths: this.columnWidths,
      hidden: Array.from(this.hiddenColumns),
      sortKeys: this.sortKeys,
      pageSize: this.pageSize,
    };
    try { localStorage.setItem(this.prefsKey, JSON.stringify(prefs)); } catch { /* quota — ignore */ }
  }
  private restoreUserPrefs(): void {
    if (!this.enableSavePrefs) return;
    try {
      const raw = localStorage.getItem(this.prefsKey);
      if (!raw) return;
      const p = JSON.parse(raw) as Record<string, unknown>;
      if (Array.isArray(p['columnOrder'])) this.columnOrder = (p['columnOrder'] as unknown[]).map(String);
      if (p['columnWidths'] && typeof p['columnWidths'] === 'object') this.columnWidths = p['columnWidths'] as Record<string, number>;
      if (Array.isArray(p['hidden'])) this.hiddenColumns = new Set((p['hidden'] as unknown[]).map(String));
      if (Array.isArray(p['sortKeys'])) {
        this.sortKeys = (p['sortKeys'] as Array<{ key: string; dir: 'asc' | 'desc' }>);
        this.sortKey = this.sortKeys[0]?.key || '';
        this.sortDir = this.sortKeys[0]?.dir || '';
      }
    } catch { /* corrupt — ignore */ }
  }

  // ── File upload (file → compute API → table re-renders) ────────────────────
  /** Toolbar Upload button → open the hidden file picker. */
  triggerUpload(): void {
    if (this.uploading) return;
    this.fileInputRef?.nativeElement?.click();
  }

  /** File picked → POST it as multipart FormData to the upload/compute API,
   *  tagged with this table's componentId + dataset, then re-render the table
   *  from the response rows. Everything config-driven; the backend returns the
   *  new data. */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    const form = new FormData();
    // Field name `file` matches the existing dataset-upload pattern; add both
    // singular + plural so either backend contract works.
    form.append('file', file, file.name);
    form.append('files', file, file.name);
    // Context so the backend knows WHICH table to update + what to return.
    const componentId = String(this.capabilities?.componentId || this.cfg['componentId'] || this.cfg.widgetId || '');
    const dataset = String(this.capabilities?.dataset || this.cfg['dataset'] || '');
    if (componentId) form.append('componentId', componentId);
    if (dataset) form.append('dataset', dataset);
    form.append('tableType', String(this.capabilities?.tableType || 'normal'));

    this.uploading = true;
    this.uploadMessage = `Uploading ${file.name}…`;
    // NOTE: do NOT set Content-Type — the browser adds the multipart boundary.
    this.http.post<unknown>(this.uploadUrl, form).subscribe({
      next: (res) => {
        this.uploading = false;
        const rows = this.extractUploadRows(res);
        if (rows) {
          this.renderRows(rows);
          this.uploadMessage = `Loaded ${rows.length} row(s) from ${file.name}`;
        } else {
          this.uploadMessage = `Uploaded ${file.name} (no rows returned)`;
        }
        // Reset the input so the same file can be re-picked.
        if (input) input.value = '';
      },
      error: () => {
        this.uploading = false;
        this.uploadMessage = `Upload failed for ${file.name}`;
        if (input) input.value = '';
      },
    });
  }

  /** Pull flat rows out of the upload/compute response (common envelope shapes:
   *  responseList[0].data, data, results[0].result.data, top-level array). */
  private extractUploadRows(res: unknown): Array<Record<string, unknown>> | null {
    if (res == null) return null;
    if (Array.isArray(res)) return res as Array<Record<string, unknown>>;
    const r = res as Record<string, unknown>;
    const list = r['responseList'] as Array<Record<string, unknown>> | undefined;
    const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
    const results = r['results'] as Array<Record<string, unknown>> | undefined;
    const fromResults = Array.isArray(results)
      ? ((results[0]?.['result'] as Record<string, unknown> | undefined)?.['data'] ?? results[0]?.['data'])
      : undefined;
    const candidate = fromList ?? fromResults ?? r['data'] ?? r['Data'] ?? r['rows'];
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown } | undefined)?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : null;
  }

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    // The action modal was reparented to <body> (see mountActionModal), so it
    // is no longer inside this component's view — Angular won't remove it when
    // the table is destroyed. Destroying while open (route change, tile
    // removed) would otherwise leave a dead backdrop covering the page.
    const overlay = this.actionOverlayRoot?.nativeElement;
    if (overlay && overlay.parentElement === document.body) overlay.remove();
        if (this.saveNoticeTimer) clearTimeout(this.saveNoticeTimer);

  }
}
